/**
 * What a Documents row says about its own processing, as data rather than JSX.
 *
 * Wording that lives in components is wording nobody can test, so every sentence, count and
 * tone the screen shows is decided here and the components below only place strings — the same
 * split `document-row-view.ts` already makes for the rest of the row.
 *
 * Two rules hold the module together:
 *
 * 1. **`now` is an argument, never `Date.now()`.** Every elapsed and relative label is a
 *    function of a clock the caller supplies, so a test can state a duration instead of
 *    approximating one, and so a row re-renders on the ticker the screen owns.
 * 2. **Absent is never zero.** `DocumentProcessingFacts` leaves out what it does not know —
 *    a workbook has no page count, a parse written before a counter carries none — and a
 *    read-out that printed `0` for those would be inventing a fact. Every row of every
 *    payload below is omitted rather than defaulted when its source is missing.
 */

import type {
  DocumentCardCompleteness,
  DocumentCardDegradedReasonKind,
  DocumentCompleteness,
  DocumentDescriptionCompleteness,
  DocumentEmbeddedObjectsCompleteness,
  DocumentFigureDrops,
  DocumentPageRenderCompleteness,
  DocumentProbeFormat,
  DocumentProcessingFacts,
  DocumentProcessingStage,
  DocumentReadTheRestOffer,
  EmbeddedObjectLine,
  EmbeddedProvenance,
  FigureDropDetail,
  FigureDropReason,
  PageRenderDropDetail,
  PageRenderDropReason,
  ProbeFacts,
  ReadPageRange,
  ReadScope,
} from "@repo/shared";
import {
  classifyArtifactFile,
  countPageRanges,
  DOC_PROCESSING_CHUNK_PAGES_DEFAULT,
  deriveDocumentCardDegradedReasonKind,
  describeEmbeddedObjectListedReason,
  describeSheetsNotRead,
  FIGURE_DROP_REASONS,
  formatEmbeddedWhere,
  formatPageRangeList,
  formatProcessingProgress,
  formatRenderLabel,
  isDocumentPicturesUnavailable,
  isDocumentRetryOffered,
  isDocumentRetryOfferedForFile,
  isEmbeddedRefusalPermanent,
  isMediaArtifactFile,
  mergePageRanges,
  PRE_READ_SUGGESTED_SPAN,
  presentSpanCount,
  readScopePageRanges,
} from "@repo/shared";
import { getErrorMessage } from "@/lib/app-errors";
import { getAppErrorData } from "@/lib/convex-errors";
import { formatDuration, formatFileSize, formatLanguageNames } from "@/lib/formatters";
import { documentPreviewMenuLabel } from "./document-preview-view";
import { primaryEmbeddedProvenance } from "./document-provenance";
import {
  type DocumentCitationCarryover,
  type DocumentExternalSourceCheck,
  type DocumentStatus,
  documentOpenSourceFromRow,
  type ProjectDocumentRow,
  resolveDocumentOpenAction,
} from "./document-row-view";
import {
  GOOGLE_IMPORT_COPY,
  isGoogleOwnedDocument,
  resolveGoogleOpenLabel,
  resolveGoogleRefreshLabel,
} from "./google-import-view";

/* -------------------------------------------------------------------------- */
/* Copy                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The fixed strings the popovers and the status cell show, in one object.
 *
 * Section headings and button labels are copy too: a component that spelled "WHAT WE READ"
 * itself would be a second place the designer's wording lives, and the first one to drift.
 */
export const DOCUMENT_PROCESSING_COPY = {
  progressHeading: "PROGRESS",
  citationsChecking: "Checking citations",
  citationsComplete: "Citations verified and updated",
  citationsFailed: "Citation check failed",
  foundSoFarHeading: "FOUND SO FAR",
  whatWeReadHeading: "WHAT WE READ",
  whatWeKnowHeading: "WHAT WE KNOW",
  readScopeHeading: "HOW MUCH SHOULD WE READ?",
  preReadPrompt: "This one is unusually large. Tell us how much to read before we start.",
  /** The same question, asked a second time about a document part of which is already read. */
  readMoreHeading: "HOW MUCH MORE SHOULD WE READ?",
  readMorePrompt: "Part of this one is read. Tell us how much more to read.",
  foundSoFarNote: "Counted as each part finishes. Tables and word count are only known at the end.",
  stop: "Stop",
  tryAgain: "Try again",
  /**
   * The other way to end a long read, and the wording carries the whole difference from Stop
   * processing: that one cancels and throws away every part already converted, this one keeps
   * them and leaves a readable — partly-read — document behind.
   */
  stopAndKeep: "Stop and keep what's read",
  /**
   * Said on the row between the request and the part boundary that honours it.
   *
   * "Then stopping" rather than "Stopping": a part is still converting, and a row that claimed
   * to have stopped while its progress bar moved would be the screen contradicting itself.
   */
  stoppingAfterCurrentPart: "Finishing the current part, then stopping…",
  /**
   * A large document that stepped aside at a wave boundary so the files queued behind it could
   * be read first.
   *
   * Deliberately not "Queued" and deliberately not "retrying": nothing bounced this file and
   * nobody has to do anything about it. The sentence beneath says that it comes back on its
   * own, because a wait with no stated end reads as a document somebody has to rescue.
   */
  lettingOthersThrough: "Letting other files through",
  yieldedToOtherFiles: "Paused so other files can be read first. It resumes on its own.",
  /**
   * The two repairs a partly-read document can be offered, worded as the two different things
   * they are.
   *
   * "Read more…" ends in an ellipsis because it opens a question rather than starting a run —
   * the one convention that separates a command from a request for a decision. "Read all
   * pictures" names its own outcome instead: nothing is asked, and the sentence beside it says
   * what it costs.
   */
  readMore: "Read more…",
  /**
   * The newest attempt to read this file again — "Read more…", "Read all pictures", Reprocess —
   * failed, and nothing about the row changed.
   *
   * Worded for all three presses rather than for "Read more…" alone, because one backend fact
   * covers them: a row that named the wrong press would be inventing a detail to sound specific.
   * The suffix is said on the cell because the failure has to be visible without opening
   * anything — the row otherwise says exactly what it said before the press.
   */
  lastAttemptFailedSuffix: "last attempt failed",
  lastAttemptFailed:
    "The last attempt to read this file again failed. What was read before is still available.",
  readAllPictures: "Read all pictures",
  readAllPicturesNote: "This collects the pictures that were skipped.",
  reprocess: "Reprocess",
  /**
   * Appended to the "could not be opened" sentence, because that shortfall is the one with a
   * repair the reader would not otherwise guess. "Read more…" resumes the parts holding those
   * pictures exactly as they are, so only a full re-read puts the images back.
   */
  reprocessToReadPictures: "Reprocess the document to read them again.",
  deleteUpload: "Delete upload",
  startReading: "Start reading",
  /** The pre-read question's own submit, worded for the second asking. */
  readMoreSubmit: "Read more",
  selectAllSheets: "Select all",
  clearSheets: "Clear",
  /** Said beside a worksheet the workbook itself hides — the checkbox is disabled with it. */
  hiddenSheet: "hidden",
  /** Said beside a worksheet this document has already been read for — ticked and locked. */
  alreadyReadSheet: "read",
  chooseScope: "Choose what to read",
  showOnlyWaiting: "Show only these",
  waitingFilterChip: "Waiting for you",
  /** Said only where a retry can actually reuse converted parts — see `buildFailedPopover`. */
  retryReusesParts:
    "Trying again usually helps: the parts that were already read are reused, so it starts further along.",
  /** Appended to a failure a retry may fix, because already-read parts are never thrown away. */
  notReadableYet: "The document isn't readable yet.",
  allReadersBusy: "All readers are busy.",
  /**
   * The files a document carries inside itself — one row of counts, and a list behind it.
   *
   * The heading is the section's, the labels below are each object's. Every one of them is here
   * rather than in the component for the reason the rest of this object exists: a sentence about
   * what happened to somebody's file is a decision, and decisions in JSX are untestable.
   */
  embeddedHeading: "EMBEDDED FILES",
  /**
   * Said at the top of a *child's* own panel, above everything the read-out reports.
   *
   * It is the one line there that is not about this document's processing: it says which
   * document these bytes came out of, which nothing else on a child's row carries once the
   * panel covers the provenance line behind it.
   */
  embeddedInPrefix: "Embedded in ",
  /** The same fact about a workbook that has since been read again without the object. */
  embeddedNoLongerInPrefix: "No longer in ",
  embeddedLabel: "Embedded files",
  /** What the parent's bytes yielded — a fact about the extraction and about nothing after it. */
  embeddedExtracted: "extracted",
  /**
   * What the child document is doing now, read from its own rows at read time.
   *
   * Said beside the extraction state rather than instead of it: an object that was extracted and
   * a child that is readable are two facts, and a count of the first has never been evidence of
   * the second.
   */
  embeddedChildQueued: "queued",
  embeddedChildProcessing: "being read",
  embeddedChildReady: "ready",
  embeddedChildFailed: "failed",
  embeddedChildDeleted: "deleted",
  /** Registered against the parent, with no row of its own to open yet. */
  embeddedChildUnplaced: "not placed yet",
  /** The inventory keeps the entry after the child goes, and offers to make it again. */
  embeddedDeleted: "extracted, then deleted",
  embeddedPending: "being extracted now",
  embeddedAbandoned: "the attempt that would have extracted it did not finish",
  embeddedAbsent: "this document's current bytes no longer carry it",
  /** The two presses an object can be offered, worded as the two different things they are. */
  embeddedExtract: "Extract",
  embeddedExtractAgain: "Extract again",
  /** Continue a *discovery*, which is a different state from "more are listed". */
  embeddedInspect: "Inspect further",
} as const;

/** The four beats the design names, in pipeline order. Nothing reveals parts or chunks. */
export const DOCUMENT_PROGRESS_STAGE_LABELS = [
  "Queued",
  "Reading",
  "Describing pictures",
  "Writing summary",
] as const;

/**
 * The same four beats for a recording, and it differs in exactly two of them.
 *
 * **"Transcribing", not "Reading".** A recording is read by being transcribed, and a row that
 * says "Reading" over an mp4 describes work nobody would recognise — the wait is minutes to
 * hours at a transcription service, and naming it is the difference between a row a person can
 * interpret and one they cannot.
 *
 * **No "Describing pictures".** `null` rather than a skipped beat, because the pipeline mints no
 * `describe_figures` stage for a recording at all: the step does not run, it does not exist, and
 * drawing it greyed out would promise a beat that is never coming.
 *
 * The array keeps four slots so the *index space* is the pipeline's own — every `STAGE_*`
 * constant, every override key and `resolveActiveStageIndex` mean the same thing on either
 * list — and the absent beat is dropped when the steps are rendered, not when they are indexed.
 */
const RECORDING_PROGRESS_STAGE_LABELS = [
  "Queued",
  "Transcribing",
  null,
  "Writing summary",
] as const;

const STAGE_COUNT = DOCUMENT_PROGRESS_STAGE_LABELS.length;
const STAGE_QUEUED = 0;
const STAGE_READING = 1;
const STAGE_DESCRIBING = 2;
const STAGE_SUMMARY = 3;

/**
 * Which reader is reading this file — the one thing the whole module's wording forks on.
 *
 * Classified from the *uploaded* filename and MIME type through the shared classifier, the same
 * pair and the same helper the row's icon is chosen with, so a renamed recording is still a
 * recording. Absent fields read as a document: a surface that has not started sending them yet
 * keeps exactly the wording it had.
 */
function progressStageLabels(row: DocumentStatusSource): readonly (string | null)[] {
  return isRecordingRow(row) ? RECORDING_PROGRESS_STAGE_LABELS : DOCUMENT_PROGRESS_STAGE_LABELS;
}

function isRecordingRow(row: { fileName?: string; mimeType?: string | null }): boolean {
  return row.fileName === undefined
    ? false
    : isMediaArtifactFile(row.fileName, row.mimeType ?? undefined);
}

/**
 * A recording's length, in the timecode a media player shows.
 *
 * The fact a media row carries where a document row carries a page count, and it is on the row
 * from the worker's probe onwards — long before a word has been transcribed, which is exactly
 * when a person watching wants it.
 */
function formatRecordingLength(processing: DocumentProcessingFacts | undefined): string | null {
  const durationSeconds = processing?.media?.durationSeconds;
  if (durationSeconds === undefined) return null;
  return formatDuration(durationSeconds * 1000, { style: "media" });
}

/**
 * What a finished recording is, in the slot a document's pages, tables and pictures occupy.
 *
 * The three facts the transcription lane learns, and each of them is omitted rather than printed
 * as a zero when it is missing: a recording nobody has transcribed yet has an unknown number of
 * voices and no identified language, and "Speakers 0" would be an assertion about the recording
 * that nothing measured.
 */
function buildRecordingFactRows(
  processing: DocumentProcessingFacts | undefined
): DocumentFactRow[] {
  const rows: DocumentFactRow[] = [];
  const recordingLength = formatRecordingLength(processing);
  if (recordingLength) rows.push({ label: "Length", value: recordingLength, tone: "default" });

  const media = processing?.media;
  if (media?.speakerCount) {
    rows.push({ label: "Speakers", value: formatCount(media.speakerCount), tone: "default" });
  }
  const languages = formatLanguageNames(media?.languages);
  if (languages) rows.push({ label: "Language", value: languages, tone: "default" });
  return rows;
}

/** What a step's right-hand text says when its stage has not begun. */
const WAITING_DETAIL = "waiting";

/**
 * Which block kinds "Text sections" counts.
 *
 * The parse's own vocabulary has no such kind: it has headings, paragraphs, lists and code,
 * and the design groups all four under one honest word. Tables are deliberately outside the
 * group — they get their own row.
 */
const TEXT_SECTION_BLOCK_TYPES = ["heading", "paragraph", "list", "code"] as const;

const FAILURE_STAGE_LABELS: Record<DocumentProcessingStage, string> = {
  // The reading beat, named for the reader that was doing it. A failure names its stage
  // exactly, so this needs no file classification: the row itself says which one ran.
  transcribe_media: "Transcribing",
  parse_document: "Reading",
  describe_figures: "Describing pictures",
  generate_document_card: "Writing summary",
};

const FAILURE_STAGE_STEPS: Record<DocumentProcessingStage, keyof DocumentProcessingFacts["steps"]> =
  {
    // The transcription is a recording's reading stage, and `processing.steps.parse` is where
    // the backend reports it — one step key for whichever stage read the file.
    transcribe_media: "parse",
    parse_document: "parse",
    describe_figures: "describeFigures",
    generate_document_card: "card",
  };

/* -------------------------------------------------------------------------- */
/* Formatting                                                                 */
/* -------------------------------------------------------------------------- */

const COUNT_FORMAT = new Intl.NumberFormat("en-US");

/** "1,240" — thousands separators, always, because these numbers are read at a glance. */
export function formatCount(value: number): string {
  return COUNT_FORMAT.format(Math.round(value));
}

/**
 * A duration in the design's own units: "40 s", "4 min", "1 h 5 min".
 *
 * Floored rather than rounded at every step, so a run says "4 min" until it has genuinely
 * been five — a progress line that over-claims is the one people notice.
 */
export function formatElapsed(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** "4 min ago", from the same units as `formatElapsed`. */
export function formatRelative(now: number, timestamp: number): string {
  return `${formatElapsed(now - timestamp)} ago`;
}

/**
 * "Checked 4 min ago" — when this document's external source was last looked at, and nothing
 * more.
 *
 * Deliberately not a claim about the file *now*: the provider is asked when somebody presses
 * Refresh and is never polled, so the honest sentence names the moment of the last look. A check
 * that could not reach the provider says that instead, because "checked 4 min ago" over a failed
 * check reports a lookup that never happened.
 *
 * `null` for a document nobody has checked since it was imported, which is every imported row
 * until the first Refresh — and for every uploaded file, which has no source to check.
 */
export function resolveExternalSourceCheckLine(
  check: DocumentExternalSourceCheck | undefined,
  now: number
): string | null {
  if (!check) return null;
  if (check.outcome === "failed") return GOOGLE_IMPORT_COPY.refreshCheckFailed;
  return `Checked ${formatRelative(now, check.lastCheckedAt)}`;
}

/**
 * What became of this document's citations since its newest version was published: being
 * checked, done, or failed. `null` when no run is about the version on screen.
 *
 * The run counts citations as it reaches them rather than knowing the total up front, so while it
 * runs the line says how many it has checked so far.
 */
export function resolveCitationCarryoverLine(
  run: DocumentCitationCarryover | undefined
): string | null {
  if (!run) return null;
  switch (run.status) {
    case "pending":
    case "running":
      return run.assessed > 0
        ? `${DOCUMENT_PROCESSING_COPY.citationsChecking}, ${formatCount(run.assessed)} so far`
        : DOCUMENT_PROCESSING_COPY.citationsChecking;
    case "complete":
      return DOCUMENT_PROCESSING_COPY.citationsComplete;
    case "failed":
      return DOCUMENT_PROCESSING_COPY.citationsFailed;
    case "superseded":
      return null;
  }
}

/**
 * A worksheet's row count as the sheet picker shows it: "12,480 rows", "1.2M rows".
 *
 * Millions collapse because a raw POS dump has seven digits and the picker is a narrow
 * scrolling list — the exact number is not what the choice turns on.
 */
export function formatSheetRows(rows: number): string {
  if (rows >= 1_000_000) {
    const millions = rows / 1_000_000;
    const label =
      millions >= 10 ? String(Math.round(millions)) : String(Number(millions.toFixed(1)));
    return `${label}M rows`;
  }
  return `${formatCount(rows)} rows`;
}

/* -------------------------------------------------------------------------- */
/* Payload types                                                              */
/* -------------------------------------------------------------------------- */

/**
 * How loudly a number, a word or a bar is saying something.
 *
 * `warning` is reserved for "something went wrong or is missing" — a cap that bit, a read that
 * stopped short of what was asked for, a summary that could not be written. A read that holds
 * less than the whole file *because somebody said so* is not that: it is `muted`, which states
 * the fact in the same grey the row's labels use and draws no alert glyph. The distinction is
 * the whole point of the completeness read-out — it has to answer "did something go wrong?",
 * not "is this less than the whole file?".
 */
export type DocumentStatusCellTone = "default" | "destructive" | "warning" | "muted";

export type DocumentProgressStepState = "done" | "active" | "waiting";

/** One row of the PROGRESS list: the beat, where it is, and its right-hand text. */
export type DocumentProgressStep = {
  label: string;
  state: DocumentProgressStepState;
  /** "waited 2 min", "part 7 of 50", "paused", "retrying (3 of 12)", "waiting", or "". */
  detail: string;
};

/** A `label: value` line — the FOUND SO FAR, WHAT WE READ and WHAT WE KNOW blocks. */
export type DocumentFactRow = {
  label: string;
  value: string;
  tone: DocumentStatusCellTone;
  /** A thin bar under the row, 0–1, where a count was cut short of what was found. */
  fraction?: number;
};

/**
 * The fan-out detail under an active Reading step. Absent for anything read in one piece.
 *
 * **Everything here is about the run in progress**, and the backend has already taken the parts
 * it inherited out of the counts: a widening that converts pages 61–66 of an already-read 1–60
 * is "Reading pages 61–66" the instant it starts, not a run two-thirds finished before it began.
 */
export type DocumentPartsLine = {
  /**
   * The Reading step's own right-hand text: the part actually in flight, "Reading pages
   * 200–259" — the same string the step carries as its detail. Never "part k of n": a person
   * watching a run wants to know which pages are turning, not an index into a plan they cannot
   * see. Falls back to the part counter, "part 7 of 50", only where nothing can place a part on
   * any axis — a workbook, a deck whose plan carries no slide ranges, or a plan this build
   * cannot reproduce.
   */
  detail: string;
  /**
   * The full range(s) this run converts, merged into contiguous spans: "Processing pages
   * 200–300", "Processing pages 200–300, 350–360". `null` when nothing can place the run on an
   * axis.
   *
   * Always the whole run, never just what is left from the part in flight onward — the person
   * watching wants to know the shape of the work, and that does not shrink as parts land. A run
   * whose ranges the backend could not name falls back to the count, "About 420 of 3,000 pages"
   * — with the chosen span beside it where one is narrower than the file, so the leading number
   * cannot be read as a share of the whole document.
   */
  pages: string | null;
  fraction: number;
};

export type DocumentFoundSoFar = {
  rows: DocumentFactRow[];
  /** The caveat under the tallies. Absent where a read has stopped counting. */
  note: string | null;
};

export type DocumentReadingPopover = {
  /**
   * Which document these bytes came out of, for a child a workbook carried. `null` for every
   * ordinary upload. Drawn above the panel's own report, because it is the one line here that
   * is not about this document's processing.
   */
  embeddedIn: DocumentEmbeddedHeader | null;
  kind: "reading";
  title: string;
  steps: DocumentProgressStep[];
  partsLine?: DocumentPartsLine;
  foundSoFar?: DocumentFoundSoFar;
  /** "Started 4 min ago", or `null` when nothing recorded a start. */
  footer: string | null;
  canStop: boolean;
};

export type DocumentQueuedPopover = {
  /**
   * Which document these bytes came out of, for a child a workbook carried. `null` for every
   * ordinary upload. Drawn above the panel's own report, because it is the one line here that
   * is not about this document's processing.
   */
  embeddedIn: DocumentEmbeddedHeader | null;
  kind: "queued";
  title: string;
  steps: DocumentProgressStep[];
  /** "retrying (3 of 12)", or `null` for a document that has not been bounced. */
  retryLabel: string | null;
  /**
   * Why this document is waiting, under the step it is waiting on: "Next try in about a minute.
   * All readers are busy." for a saturated worker, or the yield sentence for a large read that
   * gave way to the files behind it. `null` for a document simply waiting its turn, which needs
   * no explanation.
   */
  waitLine: string | null;
  /** "Waiting 6 min". */
  footer: string | null;
  canStop: boolean;
};

/**
 * How loudly one sentence about a missing picture is said.
 *
 * `warning` is a loss: content that is not in the read and would have been. `muted` is a fact
 * about the file — a glyph too small to carry anything, a logo that repeats — which is worth
 * saying once and is never a defect.
 */
export type DocumentPictureNoticeTone = "warning" | "muted";

export type DocumentPictureNotice = { text: string; tone: DocumentPictureNoticeTone };

export type DocumentCompletenessPopover = {
  /**
   * Which document these bytes came out of, for a child a workbook carried. `null` for every
   * ordinary upload. Drawn above the panel's own report, because it is the one line here that
   * is not about this document's processing.
   */
  embeddedIn: DocumentEmbeddedHeader | null;
  kind: "completeness";
  title: string;
  /**
   * The one sentence about something that happened *after* this read — the newest attempt to
   * replace it failed. Drawn first, above everything else in the panel, because it is the only
   * line here that is not about the version on screen: the rest of the panel reports what was
   * read, and this says why it is still what you are looking at.
   */
  alert: string | null;
  rows: DocumentFactRow[];
  /** The one sentence saying what went wrong or is missing. `null` when nothing did. */
  warning: string | null;
  /**
   * What became of the pictures this read did not keep — one sentence per reason group, each in
   * its own voice, and empty for a read that kept every picture it found.
   *
   * Separate from `warning` because these sentences are not all warnings: a picture left out for
   * being 37 × 35 pixels and a picture that failed to upload are two different facts, and a
   * panel that said both in amber would be asking somebody to repair a bullet point.
   */
  pictures: DocumentPictureNotice[];
  /**
   * What became of the views of a sheet this read did not ship — one sentence per reason and
   * sheet, in the same two voices the picture notices use: a warning for a view that is
   * missing, a note for a sheet that simply has no image showing it whole.
   *
   * Empty for a document whose renders are pages or slides: those are accounted for by the
   * cap's own sentence, which counts pages.
   */
  renders: DocumentPictureNotice[];
  /**
   * The worksheets this read left out, named, one sentence per reason
   * (`describeSheetsNotRead`, the words Claudia is given too). Grey for a chart sheet, which
   * holds no cells and so loses nothing; a warning for any sheet whose cells are not here.
   */
  sheetsNotRead: DocumentPictureNotice[];
  /**
   * The one sentence stating a *chosen* narrowing — the span somebody asked for, and what it
   * left out. Neutral: it is drawn without the warning glyph, because nothing about it needs
   * repairing. Separate from `warning` so a read that was both scoped by choice and cut by a
   * cap can say both things, each in its own voice.
   */
  note: string | null;
  /** "Finished 4 min ago · took 41 min". */
  footer: string | null;
  /**
   * What became of the citations of this document's older versions: "Checking citations, 12 so
   * far", "Citations verified and updated", or the failure. Its own line rather than a fact of the
   * read, because it is background work after publication that the row's status never waits for.
   */
  citations: string | null;
  /**
   * "Checked 4 min ago" — when this document's external source was last looked at. `null` for an
   * uploaded file, which has no source, and for an import nobody has refreshed yet.
   *
   * It is the one line in this panel that is not about the read: the rest reports what was read
   * out of the bytes on screen, and this says when we last asked whether those are still the
   * file's.
   */
  sourceCheck: string | null;
  readTheRest?: DocumentReadTheRestOffer;
  /**
   * What pressing the footer's offer will do, said before it is pressed — and only where the
   * answer is not obvious. `limit` re-reads the whole file to collect the pictures it skipped,
   * which is worth stating; `scope` opens a question, and the question explains itself.
   */
  readTheRestNote: string | null;
  /**
   * The failed attempt is repairable, so the footer offers Try again in place of the two
   * read-the-rest presses. It wins because it is the newer fact: the offers below it describe a
   * read that is now one failed attempt out of date, and re-running that attempt is what the
   * person just asked for and did not get.
   */
  retryOffered: boolean;
  /**
   * The last-resort repair: read the whole file again from scratch.
   *
   * Ranked below the two read-the-rest presses *only* when neither of them would be repairing a
   * lie — see `picturesUnavailable` for the one case where this jumps ahead of them instead.
   */
  reprocessOffered: boolean;
  /**
   * This read's pictures cannot be opened — `completeness.descriptions.reasonKind ===
   * "image_unavailable"` — which is the one shape of `reprocessOffered` neither read-the-rest
   * press can fix: "Read more…" resumes the very parts holding those pictures unchanged, and
   * "Read all pictures" re-reads under today's caps without touching a stored image that is
   * simply gone. `resolveCompletenessFooterAction` reads this to move Reprocess ahead of both
   * offers for exactly this shape, and leaves it last — the outdated-parse case — otherwise.
   */
  picturesUnavailable: boolean;
};

export type DocumentFailedPopover = {
  /**
   * Which document these bytes came out of, for a child a workbook carried. `null` for every
   * ordinary upload. Drawn above the panel's own report, because it is the one line here that
   * is not about this document's processing.
   */
  embeddedIn: DocumentEmbeddedHeader | null;
  kind: "failed";
  title: string;
  sentence: string;
  rows: DocumentFactRow[];
  /** The "already-read parts are reused" note — only where that is true. */
  retryNote: string | null;
  /** "Failed 6 min ago". */
  footer: string | null;
  retryOffered: boolean;
};

export type DocumentReadScopeOptionKind =
  | "everything"
  | "pages"
  | "slides"
  | "text_only"
  | "sheets";

export type DocumentReadScopeOption = {
  kind: DocumentReadScopeOptionKind;
  label: string;
  /** The line under the label. `null` for "Pick sheets", whose list is its description. */
  description: string | null;
  /** Bounds and the suggested first bite, for the page/slide range inputs. */
  range?: { min: number; max: number; defaultStart: number; defaultEnd: number };
};

export type DocumentPreReadSheet = {
  name: string;
  index: number;
  hidden: boolean;
  /** "12,480 rows". */
  rowsLabel: string;
  /**
   * This worksheet is already read. Ticked and locked in the second asking: unticking it would
   * be asking to *un*read something, which "Read more" cannot do.
   */
  alreadyRead: boolean;
};

/**
 * Which asking this is.
 *
 * `pre_read` is the question a large file asks before anything has been read; `extend` is the
 * same question asked again about a document part of which is already read ("Read more…").
 * They share every mechanic — the same options per format, the same range boxes, the same sheet
 * list — and differ in exactly what the difference between them implies: the header states what
 * was read, "Text only" is not offered (depth cannot be widened by choosing a span), the range
 * starts at the first unread page, already-read sheets are ticked and locked, and there is no
 * "Delete upload" — the file has been read; deleting it is not an answer to "how much more".
 */
export type DocumentReadScopeMode = "pre_read" | "extend";

export type DocumentPreReadPopover = {
  kind: "pre_read";
  mode: DocumentReadScopeMode;
  /** The document the answer is submitted for. */
  artifactId: string;
  title: string;
  format: DocumentProbeFormat;
  prompt: string;
  /** "3,000 pages · 184 MB", or — in `extend` — "Pages 1–55 of 3,590 read". */
  header: string;
  optionsHeading: string;
  /** "Start reading" / "Read more". */
  submitLabel: string;
  options: DocumentReadScopeOption[];
  /** The workbook's worksheets, in workbook order. Empty for every non-workbook. */
  sheets: DocumentPreReadSheet[];
};

export type DocumentPopoverPayload =
  | DocumentReadingPopover
  | DocumentQueuedPopover
  | DocumentCompletenessPopover
  | DocumentFailedPopover
  | DocumentPreReadPopover;

export type DocumentStatusCellProgress = {
  /** 0–1, or `null` for a run whose position nothing can place — an indeterminate bar. */
  fraction: number | null;
  stageIndex: number;
  stageCount: number;
};

export type DocumentStatusCellAction = {
  kind: "choose_scope";
  label: string;
};

export type DocumentStatusCellView = {
  /**
   * `progress` earns the bar and the elapsed label; `action` replaces the label with a
   * button; `plain` is everything that has stopped moving, with or without a chevron.
   */
  kind: "plain" | "progress" | "action";
  label: string;
  progress?: DocumentStatusCellProgress;
  /** "4 min" — shown only while the pipeline is actually working on this file. */
  elapsedLabel?: string;
  /** Present exactly where the design draws a chevron. A plain Ready row has none. */
  popover?: DocumentPopoverPayload;
  action?: DocumentStatusCellAction;
  tone: DocumentStatusCellTone;
};

/* -------------------------------------------------------------------------- */
/* Status cell                                                                */
/* -------------------------------------------------------------------------- */

/**
 * What the status cell and its popovers actually read off a document.
 *
 * Structural rather than `ProjectDocumentRow` so the Attachments tab can feed the same
 * builders: its query answers a different shape and carries no processing facts, and every
 * payload below already omits what it cannot know.
 */
export type DocumentStatusSource = {
  /**
   * Which document this is. Two of the payloads are *questions* rather than reports, and the
   * answer is a mutation against one artifact, so the payload has to name the file it asks
   * about rather than only showing its name in a header.
   */
  artifactId: string;
  name: string;
  /**
   * The name the bytes arrived under, and the type they arrived as — the pair that says which
   * reader read this file, and therefore whether the row says "Reading" or "Transcribing".
   *
   * The *uploaded* name, never the display name, for the reason the row's icon reads it too: a
   * rename moves the label and leaves the file exactly what it was. Both optional, so a surface
   * that has not started sending them keeps the document wording it has always had.
   */
  fileName?: string;
  mimeType?: string | null;
  status: DocumentStatus;
  processing?: DocumentProcessingFacts;
  fileSizeBytes?: number | null;
  /** Present only once somebody has refreshed; the panel discloses when that was. */
  externalSourceCheck?: DocumentExternalSourceCheck;
  /** The citation carry-over run for the version on screen; only the completeness panel reads it. */
  citationCarryover?: DocumentCitationCarryover;
  /**
   * The documents this file was carried inside, and where in each of them. Absent for every row
   * that is not an embedded child, and on a surface whose query does not carry it.
   */
  embeddedIn?: readonly EmbeddedProvenance[];
};

export function buildDocumentStatusCellView(
  row: DocumentStatusSource,
  now: number
): DocumentStatusCellView {
  const status = row.status;

  switch (status.kind) {
    case "queued":
      return buildQueuedCell(row, now);
    case "parsing":
    case "summarizing":
      return buildActiveCell(row, now);
    case "waiting_for_input":
      return buildWaitingCell(row, status);
    case "ready":
      return buildReadyCell(row, now);
    case "failed":
      return buildFailedCell(row, now);
    case "cancelled":
      // A stop leaves nothing to disclose — the run's own bookkeeping went with it — so the
      // cell is the word and no chevron. The repair is the row menu's Try again; the sentence
      // explaining who stopped it is the row's status hint.
      return { kind: "plain", label: "Stopped", tone: "default" };
    default:
      // `not_processed`: nothing has read this file and nothing is going to on its own. The
      // row menu carries the repair (`Try again`); the cell has nothing to disclose.
      return { kind: "plain", label: "Not processed", tone: "default" };
  }
}

function buildQueuedCell(row: DocumentStatusSource, now: number): DocumentStatusCellView {
  const queued = row.processing?.queued;
  const popover = buildQueuedPopover(row, now);
  if (queued?.yielded) {
    // A yielded read is the one queued state that has something to show for itself: the parts
    // it converted before it stepped aside stay on the row, and naming them is what separates
    // "waiting, with 480 pages already read" from "waiting, nothing has happened".
    const partsRead = formatPartsRead(row.processing);
    return {
      kind: "plain",
      label: partsRead
        ? `${DOCUMENT_PROCESSING_COPY.lettingOthersThrough} · ${partsRead}`
        : DOCUMENT_PROCESSING_COPY.lettingOthersThrough,
      popover,
      // Stated, not flagged: giving way is the pipeline working as designed, and an amber
      // glyph over it would read as a document that needs rescuing.
      tone: "muted",
    };
  }
  const retryLabel = resolveRetryLabel(queued);
  const queuedLabel = DOCUMENT_PROGRESS_STAGE_LABELS[STAGE_QUEUED];
  return {
    kind: "plain",
    label: retryLabel ? `${queuedLabel} · ${retryLabel}` : queuedLabel,
    popover,
    tone: "default",
  };
}

function buildActiveCell(row: DocumentStatusSource, now: number): DocumentStatusCellView {
  const processing = row.processing;
  const labels = progressStageLabels(row);
  const stageIndex = resolveActiveStageIndex(row.status, processing);
  const startedAt = processing?.steps.parse?.startedAt;
  // A read somebody has asked to end says so instead of saying "Reading" — the bar is still
  // moving, and it is moving through the last part. Muted rather than a warning: the person
  // asked for this and nothing needs repairing.
  const stopping = processing?.chunks?.stopping === true;
  return {
    kind: "progress",
    label: stopping
      ? DOCUMENT_PROCESSING_COPY.stoppingAfterCurrentPart
      : (labels[stageIndex] ??
        labels[STAGE_READING] ??
        DOCUMENT_PROGRESS_STAGE_LABELS[STAGE_READING]),
    progress: {
      fraction: resolveCellFraction(processing, stageIndex),
      stageIndex,
      stageCount: STAGE_COUNT,
    },
    ...(startedAt === undefined ? {} : { elapsedLabel: formatElapsed(now - startedAt) }),
    popover: buildReadingPopover(row, now),
    tone: stopping ? "muted" : "default",
  };
}

function buildWaitingCell(
  row: DocumentStatusSource,
  status: Extract<DocumentStatus, { kind: "waiting_for_input" }>
): DocumentStatusCellView {
  // One question, asked once per file: how much of it to read. A fan-out that meets a busy
  // queue mid-read yields the shared reader and comes back on its own, so nothing else on this
  // screen ever waits on a person.
  const label = DOCUMENT_PROCESSING_COPY.chooseScope;
  return {
    kind: "action",
    label,
    action: { kind: "choose_scope", label },
    popover: buildPreReadPopover(row, status.probe),
    tone: "default",
  };
}

function buildReadyCell(row: DocumentStatusSource, now: number): DocumentStatusCellView {
  const processing = row.processing;
  const suffix = resolveReadySuffix(processing);
  const popover = buildCompletenessPopover(row, now);
  return {
    kind: "plain",
    label: suffix ? `Ready · ${suffix.label}` : "Ready",
    ...(popover ? { popover } : {}),
    tone: suffix?.tone ?? "default",
  };
}

function buildFailedCell(row: DocumentStatusSource, now: number): DocumentStatusCellView {
  return {
    kind: "plain",
    label: "Failed",
    popover: buildFailedPopover(row, now),
    tone: "destructive",
  };
}

/**
 * The three suffixes a Ready row can carry, and the tone each one is said in — `null` for a
 * read nobody has to think about, which is what keeps a plain Ready row free of a chevron.
 *
 * They are split by *meaning*, not by mechanism. "partly read" is the neutral one: a span was
 * chosen and the read holds exactly that span, so the row is reporting a decision somebody
 * made and is muted. "not fully read" is the one that wants attention — a cap cut the pictures,
 * or the read stopped short of the span it was asked for — and it is amber. A read that was
 * scoped by choice *and* cut by a cap says the loud thing: the choice is still true, but it is
 * no longer the whole story, and the popover's note says it in full.
 *
 * "last attempt failed" outranks both, because it is the *newest* fact and the only one the
 * person is waiting on an answer about: they pressed something, the row went back to saying
 * what it said before, and until this is on the cell the press is invisible. What is missing
 * from the read is still true and is still said in full inside the panel.
 */
type DocumentReadySuffix = { label: string; tone: DocumentStatusCellTone };

function resolveReadySuffix(
  processing: DocumentProcessingFacts | undefined
): DocumentReadySuffix | null {
  if (!processing) return null;
  if (processing.lastReadAttemptFailed) {
    return { label: DOCUMENT_PROCESSING_COPY.lastAttemptFailedSuffix, tone: "warning" };
  }
  const completeness = processing.completeness;
  if (resolveReadTheRest(processing) === "limit" || hasPictureLimitLoss(completeness)) {
    return { label: "not fully read", tone: "warning" };
  }
  const shortSpan =
    processing.readTheRest === "scope" || completeness?.coverage?.status === "partial";
  if (!shortSpan) return null;
  return isReadAsChosen(processing)
    ? { label: "partly read", tone: "muted" }
    : { label: "not fully read", tone: "warning" };
}

/** Whether this read is short of content the reader found — pictures or page photos. */
function hasPictureLimitLoss(completeness: DocumentCompleteness | undefined): boolean {
  if (!completeness) return false;
  if (hasPictureContentLoss(completeness)) return true;
  return countRenderContentLoss(completeness.pageRenders) > 0;
}

/**
 * Whether a picture carrying content is missing from this read.
 *
 * The question every amber pixel about pictures turns on, and it is answered from the *reasons*
 * the pictures were dropped for, never from the shortfall alone: a deck that drops the same logo
 * off 60 slides and one that drops 60 photographs both read "58 of 72", and only one of them has
 * lost anything. A revision written before the reader attributed its drops has nothing but the
 * shortfall, and is read the way it always was — including a drop the reader counted and left
 * unattributed, which is a shortfall with no reason beside it rather than a reason of "none".
 */
function hasPictureContentLoss(completeness: DocumentCompleteness): boolean {
  const figures = completeness.figures;
  if (!figures.dropped) return figures.found > figures.kept;
  return (
    countDrops(figures.dropped, PICTURE_LOSS_DROP_REASONS) > 0 ||
    unattributedDrops(figures.dropped) > 0
  );
}

/**
 * Reasons a dropped picture is a loss: content that would have been read and is not.
 *
 * The complement is the two glyph reasons below — a bullet, a rule, a spacer — which are the
 * whole reason this list exists rather than a shortfall count. `decorative` is in neither: the
 * reader sets those aside before any budget sees them, and they are not drops at all.
 */
const PICTURE_LOSS_DROP_REASONS = [
  "undecodable",
  "linked",
  "upload_failed",
  "byte_budget",
  "cap",
] as const satisfies readonly FigureDropReason[];

/** Reasons a second read of the same bytes puts the picture back. */
const PICTURE_RECOVERABLE_DROP_REASONS = [
  "upload_failed",
  "byte_budget",
  "cap",
] as const satisfies readonly FigureDropReason[];

/** Reasons the picture was never content: a bullet, a rule, a spacer. */
const PICTURE_GLYPH_DROP_REASONS = [
  "too_small",
  "too_few_bytes",
] as const satisfies readonly FigureDropReason[];

function countDrops(drops: DocumentFigureDrops, reasons: readonly FigureDropReason[]): number {
  return reasons.reduce((total, reason) => total + (drops.byReason[reason] ?? 0), 0);
}

/**
 * The drops this reader counted and gave no reason for.
 *
 * `byReason` is an attribution, not a verdict: a reader that named reasons for some of its drops
 * has said nothing at all about the rest, and a reader too old to attribute anything sends an
 * empty map over a real shortfall. Reading either as "no loss" is how missing content reaches a
 * person as a clean Ready row, so the remainder is carried as unknown and treated the way an
 * unattributed shortfall has always been treated: a warning, and whatever repair the backend
 * still offers.
 */
function unattributedDrops(drops: DocumentFigureDrops): number {
  return Math.max(0, drops.total - countDrops(drops, FIGURE_DROP_REASONS));
}

/**
 * The read-the-rest offer this row may actually make.
 *
 * `scope` passes through — a missing span is a missing span. `limit` is withheld where the
 * reader's own attribution says no press would recover anything: an undecodable icon, a linked
 * picture and a 16 × 16 bullet all come back exactly as they went, so "Read all pictures" over
 * them offers a repair that cannot happen. A revision that attributed nothing — or attributed
 * only part of its shortfall — keeps the backend's offer, which is the only evidence there is.
 *
 * Page photos are asked first and separately: they are their own budget, and the embedded
 * attribution says nothing about them. Withholding the whole offer over an icon the reader could
 * not decode is what leaves a document whose page photos hit the render cap saying they are
 * missing with nothing on screen to get them back.
 */
function resolveReadTheRest(
  processing: DocumentProcessingFacts | undefined
): DocumentReadTheRestOffer | undefined {
  const offer = processing?.readTheRest;
  if (offer !== "limit") return offer;
  const completeness = processing?.completeness;
  if (!completeness) return offer;
  if (countRenderContentLoss(completeness.pageRenders) > 0) return offer;
  const drops = completeness.figures.dropped;
  if (!drops) return offer;
  return countDrops(drops, PICTURE_RECOVERABLE_DROP_REASONS) > 0 || unattributedDrops(drops) > 0
    ? offer
    : undefined;
}

/**
 * Whether this read holds exactly what somebody asked it to hold.
 *
 * The question every tone below turns on, and it is deliberately strict: a scope *and* a
 * coverage that matches it. A read that stopped early leaves the same `partial` coverage as a
 * chosen range does, and the difference between them is the whole difference between "as you
 * chose" and "it stopped short" — so the covered range is compared against the requested one
 * rather than trusted to mean the same thing.
 *
 * The comparison is over **every** span, in order, because a scope widened around a hole is
 * still a scope somebody chose: a read of pages 1–66 and 100–105 that holds exactly those two
 * spans was answered, and calling it "stopped short" would put a warning on a document nothing
 * went wrong with.
 *
 * A `sheets` scope has no page axis to compare on: a partial workbook names no ranges, so the
 * scope's existence is the only evidence there is, and a workbook read can only end up partial
 * by somebody having picked sheets.
 */
function isReadAsChosen(processing: DocumentProcessingFacts | undefined): boolean {
  const scope = processing?.readScope;
  if (!scope) return false;
  const coverage = processing?.completeness?.coverage;
  if (coverage?.status !== "partial") return false;
  if (scope.kind === "sheets") return true;
  if (scope.kind !== "pages") return false;
  const covered = coverage.coveredPageRanges;
  if (!covered) return false;
  const total = coverage.totalPages;
  const requested = readScopePageRanges(scope).map((range) => {
    const start = Math.max(1, Math.min(range.start, range.end));
    const end = Math.max(range.start, range.end);
    // A range that ran past the end of the file was answered with what the file has, and that is
    // still exactly what was asked for.
    return { start, end: total !== undefined && total > 0 ? Math.min(end, total) : end };
  });
  return (
    covered.length === requested.length &&
    requested.every(
      (range, index) => covered[index]?.start === range.start && covered[index]?.end === range.end
    )
  );
}

/** Which beat a running document is on. Both parse beats live under one `status.kind`. */
function resolveActiveStageIndex(
  status: DocumentStatus,
  processing: DocumentProcessingFacts | undefined
): number {
  if (processing?.steps.describeFigures?.status === "running") return STAGE_DESCRIBING;
  return status.kind === "summarizing" ? STAGE_SUMMARY : STAGE_READING;
}

/**
 * The bar's fill.
 *
 * A large document knows exactly where it is — parts converted of parts planned — and every
 * other document knows only which beat it is on, so its bar sits at the midpoint of that
 * beat's share: a stage that has begun is neither nothing nor done. Never a percentage in
 * text; the number only ever drives a bar.
 */
function resolveCellFraction(
  processing: DocumentProcessingFacts | undefined,
  stageIndex: number
): number | null {
  const chunks = processing?.chunks;
  if (chunks && chunks.total > 0 && stageIndex === STAGE_READING) {
    return clampFraction(chunks.completed / chunks.total);
  }
  // Nothing named a step at all: the row is being processed and cannot say where. An
  // indeterminate bar is the honest drawing of that.
  if (!processing || Object.keys(processing.steps).length === 0) return null;
  return clampFraction((stageIndex + 0.5) / STAGE_COUNT);
}

function clampFraction(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/* -------------------------------------------------------------------------- */
/* Progress steps                                                             */
/* -------------------------------------------------------------------------- */

type StepDetailOverrides = Partial<Record<number, string>>;

/**
 * The steps a panel lists, from the beats *this* file's reader actually has.
 *
 * `activeIndex` stays an index into the pipeline's four-slot space rather than into the
 * rendered list, so every caller's `STAGE_*` override key means the same thing whichever list
 * it is drawn from; a beat this reader does not have (`null`) is dropped after the state is
 * decided, never before.
 */
function buildProgressSteps(
  labels: readonly (string | null)[],
  activeIndex: number,
  overrides: StepDetailOverrides
): DocumentProgressStep[] {
  return labels.flatMap((label, index) => {
    if (label === null) return [];
    const state: DocumentProgressStepState =
      index < activeIndex ? "done" : index === activeIndex ? "active" : "waiting";
    const override = overrides[index];
    return [
      {
        label,
        state,
        detail: override ?? (state === "waiting" ? WAITING_DETAIL : ""),
      },
    ];
  });
}

/**
 * "waited 2 min" on a Queued step the document has already left.
 *
 * Only answerable while the queue facts are still on the row — they are cleared the moment
 * the stage leaves the queue — so a running document's Queued step usually carries no
 * detail rather than a duration nobody measured.
 */
function resolveWaitedDetail(row: DocumentStatusSource): string | undefined {
  const queuedSince = row.processing?.queued?.queuedSince;
  const startedAt = row.processing?.steps.parse?.startedAt;
  if (queuedSince === undefined || startedAt === undefined || startedAt < queuedSince) {
    return undefined;
  }
  return `waited ${formatElapsed(startedAt - queuedSince)}`;
}

/**
 * "retrying (3 of 12)", for a document the shared reader keeps turning away.
 *
 * A yielded read is never labelled this, whatever the row's counter says: it spent none of that
 * patience — it gave the reader away — and counting it towards a budget that ends in a failure
 * would be the screen inventing a problem.
 */
function resolveRetryLabel(queued: DocumentProcessingFacts["queued"] | undefined): string | null {
  if (!queued || queued.yielded || queued.saturationRequeueCount <= 0) return null;
  return `retrying (${queued.saturationRequeueCount} of ${queued.maxRequeues})`;
}

/** "8 of 50 parts read" — what a paused or yielded fan-out has to show for itself. */
function formatPartsRead(processing: DocumentProcessingFacts | undefined): string | null {
  const chunks = processing?.chunks;
  if (!chunks || chunks.total <= 0) return null;
  return `${formatCount(chunks.completed)} of ${formatCount(chunks.total)} parts read`;
}

/**
 * Why this document is waiting, in one line — or `null` for one that is simply waiting its turn.
 *
 * Two waits, two sentences, and telling them apart is the whole point. A *yielded* read stepped
 * aside on purpose and comes back by itself, so it says so. A *saturated* one keeps being turned
 * away by a busy reader; its delay is jittered per requeue, so that sentence rounds rather than
 * counts down — the fact a person needs is "soon, and it is not your file's fault".
 */
function resolveWaitLine(
  queued: DocumentProcessingFacts["queued"] | undefined,
  now: number
): string | null {
  if (!queued) return null;
  if (queued.yielded) return DOCUMENT_PROCESSING_COPY.yieldedToOtherFiles;
  if (queued.saturationRequeueCount <= 0) return null;
  if (queued.nextAttemptAt === undefined) return DOCUMENT_PROCESSING_COPY.allReadersBusy;
  const remaining = queued.nextAttemptAt - now;
  const minutes = Math.max(1, Math.round(remaining / 60_000));
  const span = minutes === 1 ? "about a minute" : `about ${minutes} minutes`;
  return `Next try in ${span}. ${DOCUMENT_PROCESSING_COPY.allReadersBusy}`;
}

/* -------------------------------------------------------------------------- */
/* Reading and queued popovers                                                */
/* -------------------------------------------------------------------------- */

function buildReadingPopover(row: DocumentStatusSource, now: number): DocumentReadingPopover {
  const processing = row.processing;
  const activeIndex = resolveActiveStageIndex(row.status, processing);
  const partsLine = buildPartsLine(processing);
  const describingDetail = resolveDescribingStepDetail(processing);
  const waited = resolveWaitedDetail(row);
  const startedAt = processing?.steps.parse?.startedAt;
  const foundSoFar = buildRunningFoundSoFar(processing);
  const recordingLength = formatRecordingLength(processing);

  return {
    kind: "reading",
    title: row.name,
    embeddedIn: buildEmbeddedHeader(row),
    steps: buildProgressSteps(progressStageLabels(row), activeIndex, {
      ...(waited ? { [STAGE_QUEUED]: waited } : {}),
      // The step's label already says "Reading", so its detail is the span or count alone; the
      // parts line below the steps keeps the whole phrase.
      ...(partsLine && activeIndex === STAGE_READING
        ? { [STAGE_READING]: resolveReadingStepDetail(processing, { verb: "omit" }) }
        : {}),
      // A recording has no parts to count, so the reading beat says how much audio there is —
      // the one number that tells a person how long this is going to take.
      ...(!partsLine && recordingLength ? { [STAGE_READING]: recordingLength } : {}),
      ...(describingDetail && activeIndex === STAGE_DESCRIBING
        ? { [STAGE_DESCRIBING]: describingDetail }
        : {}),
    }),
    ...(partsLine && activeIndex === STAGE_READING ? { partsLine } : {}),
    ...(foundSoFar ? { foundSoFar } : {}),
    footer: startedAt === undefined ? null : `Started ${formatRelative(now, startedAt)}`,
    canStop: true,
  };
}

function buildQueuedPopover(row: DocumentStatusSource, now: number): DocumentQueuedPopover {
  const queued = row.processing?.queued;
  const retryLabel = resolveRetryLabel(queued);
  const partsRead = queued?.yielded ? formatPartsRead(row.processing) : null;
  // A yielded read is waiting *on the Reading beat*, not on the Queued one: it has already read
  // part of the file, and putting the wait on Queued would draw a document that has done nothing.
  const activeIndex = queued?.yielded ? STAGE_READING : STAGE_QUEUED;
  return {
    kind: "queued",
    title: row.name,
    embeddedIn: buildEmbeddedHeader(row),
    steps: buildProgressSteps(progressStageLabels(row), activeIndex, {
      ...(retryLabel ? { [STAGE_QUEUED]: retryLabel } : {}),
      ...(partsRead ? { [STAGE_READING]: partsRead } : {}),
      ...(queued?.yielded ? { [STAGE_QUEUED]: resolveWaitedDetail(row) ?? "" } : {}),
    }),
    retryLabel,
    waitLine: resolveWaitLine(queued, now),
    footer:
      queued?.queuedSince === undefined
        ? null
        : `Waiting ${formatElapsed(now - queued.queuedSince)}`,
    canStop: true,
  };
}

/** The running tallies the finished parts contributed. Absent for anything read in one piece. */
function buildRunningFoundSoFar(
  processing: DocumentProcessingFacts | undefined,
  options: { note?: string | null } = {}
): DocumentFoundSoFar | undefined {
  const chunks = processing?.chunks;
  if (!chunks) return undefined;
  return {
    rows: [
      { label: "Text sections", value: formatCount(chunks.blockCount), tone: "default" },
      { label: "Pictures", value: formatCount(chunks.figureCount), tone: "default" },
    ],
    note: options.note === undefined ? DOCUMENT_PROCESSING_COPY.foundSoFarNote : options.note,
  };
}

/**
 * "Reading pages 200–259" beside the step, "Processing pages 200–300" under the bar.
 *
 * Both name the run in progress, because that is what a person watching one is waiting for:
 * `chunks` has already had the parts this read inherited subtracted out of it, so a widening
 * that converts six pages says "Reading pages 61–66" the instant it starts, never a fraction of
 * a run it did no work on.
 */
function buildPartsLine(
  processing: DocumentProcessingFacts | undefined
): DocumentPartsLine | undefined {
  const chunks = processing?.chunks;
  if (!chunks || chunks.total <= 0) return undefined;
  return {
    detail: resolveReadingStepDetail(processing),
    pages: buildProcessingPagesLine(processing),
    fraction: clampFraction(chunks.completed / chunks.total),
  };
}

/**
 * "Reading pages 200–259" — the Reading step's own right-hand text: the part actually in
 * flight, never "part k of n".
 *
 * Once every part of this run has landed and it is merging them, `currentRange` is gone —
 * nothing is left to convert — and the detail names the last part it converted rather than
 * falling silent mid-run; the merge is not itself a part with pages of its own to name.
 *
 * Falls back to the part counter, "part 7 of 50", only where nothing can place a part on any
 * axis: a workbook, a deck whose plan carries no slide ranges, or a plan this build cannot
 * reproduce. That fallback is the same "absent is never invented" rule from the other side — a
 * run whose pages nobody can place still has parts, and the counter is the honest answer to
 * "where is this" when there is no span to name.
 */
function resolveReadingStepDetail(
  processing: DocumentProcessingFacts | undefined,
  options: { verb: "keep" | "omit" } = { verb: "keep" }
): string {
  // The lane's own account of what it is doing, where it wrote one: it names the axis the run
  // counts on and the piece in flight. The two derivations below reconstruct that from the
  // fan-out's part indexes for a row carrying no such record.
  const progress = processing?.progress;
  if (progress) return formatProcessingProgress(progress, options);
  const chunks = processing?.chunks;
  const total = chunks?.total ?? 0;
  const completed = chunks?.completed ?? 0;
  const ranges = chunks?.ranges;
  const last = ranges && ranges.length > 0 ? ranges[ranges.length - 1] : undefined;
  const current = chunks?.currentRange ?? last;
  if (current) {
    const unit = resolveSpanWord(processing);
    const span = `${unit} ${formatPageRanges([current])}`;
    return options.verb === "omit" ? span : `Reading ${span}`;
  }
  return `part ${formatCount(completed)} of ${formatCount(total)}`;
}

/**
 * "12 of 40" — the Describing step's own right-hand text, read off the description stage's own
 * progress record as the Reading step's is read off the reading stage's. The label already says
 * what is being described, so the detail is the count alone.
 *
 * Guarded on the stage the record names rather than on which step is active: the row carries the
 * reading stage's record until the description stage writes its first one, and "Reading pages
 * 121–180" beside "Describing pictures" would name the wrong beat. A description stage that has
 * claimed the file but described nothing yet leaves the step bare, exactly as a read with no
 * parts to count does.
 */
function resolveDescribingStepDetail(
  processing: DocumentProcessingFacts | undefined
): string | undefined {
  const progress = processing?.progress;
  if (progress?.stage !== "describing_figures") return undefined;
  return formatProcessingProgress(progress, { verb: "omit" });
}

/**
 * "Processing pages 200–300", "Processing pages 200–300, 350–360" — the full range(s) this run
 * converts, merged into contiguous spans.
 *
 * Always the whole run, never just what is left from the part in flight onward: the shape of
 * the work does not shrink as parts land, and a person deciding whether to wait for it is asking
 * about all of it. `chunks.ranges` already excludes every part this run resumed rather than
 * converted, so merging it is the whole computation.
 *
 * Where the backend named no ranges — a workbook, a probe that counted nothing, a plan this
 * build cannot reproduce — the line falls back to the count, "About 420 of 3,000 pages", which
 * is the same "absent is never invented" rule from the other side.
 */
function buildProcessingPagesLine(processing: DocumentProcessingFacts | undefined): string | null {
  const chunks = processing?.chunks;
  const ranges = chunks?.ranges;
  if (!chunks || !ranges || ranges.length === 0) return buildPagesSoFarLine(processing);
  const unit = resolveSpanWord(processing);
  return `Processing ${unit} ${formatPageRanges(mergePageRanges(ranges))}`;
}

/** Pages or slides — the axis a file's own format counts its parts on. */
function resolveSpanWord(processing: DocumentProcessingFacts | undefined): SpanWord {
  const probe = processing?.probe;
  return probe?.format === "pptx" || probe?.slideCount !== undefined ? "slides" : "pages";
}

/** "61–66", or "1–66, 100–105" — spans in the digits the rest of the screen counts in. */
export function formatPageRanges(ranges: readonly ReadPageRange[]): string {
  return formatPageRangeList(ranges, formatCount);
}

/**
 * "1–66 and 100–105" — the same spans inside a sentence rather than beside a label.
 *
 * A prose join, because "Read pages 1–66, 100–105 only, as you chose" reads as a list that has
 * been cut off. One place, so the two joins cannot drift into three.
 */
function formatPageRangesProse(ranges: readonly ReadPageRange[]): string {
  const spans = ranges.map((range) => formatPageRanges([range]));
  if (spans.length <= 1) return spans[0] ?? "";
  return `${spans.slice(0, -1).join(", ")} and ${spans[spans.length - 1]}`;
}

/**
 * "About 420 of 3,000 pages", and the span it is counted against where one was chosen.
 *
 * The fallback for a run whose pages nothing can name. The denominator is what this run was
 * *asked* to read, never the file's own length. A 3,590-page PDF answered with pages 1–55 is
 * planned as a single 55-page part, so dividing by 3,590 read "About 0 of 3,590 pages" for a run
 * that was never going to touch page 56 — a progress line reporting a document the person had
 * already declined. Where the span is genuinely narrower than the file, the line names both,
 * because a bare "of 55" beside a file everyone knows is thousands of pages long is the same
 * misreading from the other side.
 */
function buildPagesSoFarLine(processing: DocumentProcessingFacts | undefined): string | null {
  const span = resolveReadSpan(processing);
  const soFar = resolvePagesSoFar(processing);
  const total = span ? span.length : resolveProbeSpanTotal(processing);
  if (soFar === null || !total) return null;
  const unit = span ? span.plural : "pages";
  return joinScopeClause(`About ${formatCount(soFar)} of ${formatCount(total)} ${unit}`, span);
}

/** "… · pages 1–3,000 of 3,590", appended only where the span really is narrower. */
function joinScopeClause(line: string, span: ReadSpan | null): string {
  if (!span || span.total === null || span.length >= span.total) return line;
  const range = `${formatCount(span.start)}–${formatCount(span.end)}`;
  return `${line} · ${span.plural} ${range} of ${formatCount(span.total)}`;
}

/** Parts converted × the chunk size, never past the span this run was asked to read. */
function resolvePagesSoFar(processing: DocumentProcessingFacts | undefined): number | null {
  const chunks = processing?.chunks;
  if (!chunks) return null;
  const pages = chunks.completed * DOC_PROCESSING_CHUNK_PAGES_DEFAULT;
  const ceiling = resolveReadSpan(processing)?.length ?? resolveProbeSpanTotal(processing);
  return ceiling ? Math.min(pages, ceiling) : pages;
}

/** The word a format's span is counted in — the only two axes a page range can mean. */
type SpanWord = "pages" | "slides";

/** How much of the file this run is reading, and how much of it there is. */
type ReadSpan = {
  /** The bounding span's ends — what "pages a–b of n" names, holes included. */
  start: number;
  end: number;
  /** Pages (or slides) the scope actually selects — the denominator every read-out uses. */
  length: number;
  /** What the probe measured the file at, or `null` when it counted nothing. */
  total: number | null;
  plural: SpanWord;
};

/**
 * The stretch a `pages` scope asks for, or `null` for a run nothing narrowed.
 *
 * `processing.readScope` is the job's *effective* scope — the plan's own range, already cut to
 * the probe's counts before a byte was read — so the arithmetic here restates a decision rather
 * than making a second one. It clamps again all the same: a worker one deploy behind, or a
 * range recorded against a count that later changed, must not be able to make this screen claim
 * more of a file than the file holds.
 *
 * The count is taken over the scope's **exact** ranges (`readScopePageRanges`), not the distance
 * between its two ends: widening a read can leave a hole (pages 1–55 read, then 200–260 asked
 * for), and `start`/`end` are then only the bounding span. Dividing by the bound would put pages
 * nobody asked for in the denominator and stall the line at a fraction the run can never reach.
 *
 * A `sheets` scope has no page axis to divide by (the plan reads it as one chunk, and a
 * workbook's tabs have no numbers), so it answers `null` and the read-out falls back to what
 * the probe counted — which for a workbook is nothing, and the line is omitted rather than
 * invented.
 */
function resolveReadSpan(processing: DocumentProcessingFacts | undefined): ReadSpan | null {
  const scope = processing?.readScope;
  if (scope?.kind !== "pages") return null;
  const probe = processing?.probe;
  const isSlides = probe?.format === "pptx" || probe?.slideCount !== undefined;
  const total = (isSlides ? probe?.slideCount : probe?.pageCount) ?? null;
  const ranges = readScopePageRanges(scope)
    .map((range) => {
      const low = Math.max(1, Math.min(range.start, range.end));
      const high = Math.max(range.start, range.end);
      return { start: low, end: total === null ? high : Math.min(high, total) };
    })
    .filter((range) => range.end >= range.start);
  const first = ranges[0];
  const last = ranges[ranges.length - 1];
  if (!first || !last) return null;
  const length = ranges.reduce((count, range) => count + (range.end - range.start + 1), 0);
  return {
    start: first.start,
    end: last.end,
    length,
    total,
    plural: isSlides ? "slides" : "pages",
  };
}

/** What the probe counted, for a run no scope narrowed. */
function resolveProbeSpanTotal(processing: DocumentProcessingFacts | undefined): number | null {
  return processing?.probe?.pageCount ?? null;
}

/* -------------------------------------------------------------------------- */
/* Completeness popover                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Why a summary is not the whole story — the *kind* of reason, not its prose.
 *
 * Read off the field where the pipeline recorded one, and derived from the reason string where
 * it did not, through the shared derivation rather than a copy of it: a live write and a
 * historical read that classified the same sentence two ways would be two different sentences
 * on screen for one document. Every caller below has an "absent" branch, because a card written
 * before the kind existed and carrying no reason genuinely says nothing about why.
 */
function readCardReasonKind(
  card: DocumentCardCompleteness
): DocumentCardDegradedReasonKind | undefined {
  return card.reasonKind ?? deriveDocumentCardDegradedReasonKind(card.reason);
}

/**
 * What a finished read left behind — or `undefined` for a document that was read whole,
 * which is the row the design gives no chevron.
 */
function buildCompletenessPopover(
  row: DocumentStatusSource,
  now: number
): DocumentCompletenessPopover | undefined {
  const processing = row.processing;
  const completeness = processing?.completeness;
  // An imported document that has been checked has something to disclose even when its read was
  // whole — when the source was last looked at is a fact only this panel carries, and it is the
  // one a person asks of a file that lives somewhere else too.
  const sourceCheck = resolveExternalSourceCheckLine(row.externalSourceCheck, now);
  const citations = resolveCitationCarryoverLine(row.citationCarryover);
  if (!completeness || !(hasCompletenessDisclosure(processing) || sourceCheck || citations)) {
    return undefined;
  }

  const unit = resolveSpanUnit(processing, completeness);
  const readAsChosen = isReadAsChosen(processing);
  const isRecording = isRecordingRow(row);
  const rows: DocumentFactRow[] = buildRecordingFactRows(processing);

  if (!isRecording) {
    const spanRow = buildSpanRow(completeness, unit, readAsChosen);
    if (spanRow) rows.push(spanRow);

    const textSections = countTextSections(completeness);
    if (textSections !== null) {
      rows.push({ label: "Text sections", value: formatCount(textSections), tone: "default" });
    }
    const tables = completeness.blocksByType?.table;
    if (tables !== undefined) {
      rows.push({ label: "Tables", value: formatCount(tables), tone: "default" });
    }
    // Every row from here down is about pictures, and a recording has none — not "zero of them",
    // which is what a Pictures row over a transcribed video was claiming. What a recording has
    // instead is above: its length, its voices and its languages.
    rows.push(buildPicturesRow(completeness));

    const embedded = completeness.embeddedObjects;
    if (embedded && embedded.total > 0) {
      rows.push({
        label: DOCUMENT_PROCESSING_COPY.embeddedLabel,
        value: formatEmbeddedCounts(embedded),
        tone: embedded.extracted < embedded.total ? "warning" : "default",
      });
    }

    const renders = completeness.pageRenders;
    if (renders) rows.push(...buildRenderRows(renders));
    const descriptions = completeness.descriptions;
    if (descriptions) {
      rows.push({
        // "Descriptions", not "Picture descriptions": the describer is given the rendered views
        // as well as the pictures, so the denominator counts both and the label has to as well.
        label: "Descriptions",
        value: `${formatCount(descriptions.described)} of ${formatCount(descriptions.found)}`,
        tone: descriptions.described < descriptions.found ? "warning" : "default",
      });
    }
  }
  const lastAttemptFailed = processing?.lastReadAttemptFailed === true;
  const readTheRest = resolveReadTheRest(processing);
  return {
    kind: "completeness",
    title: row.name,
    embeddedIn: buildEmbeddedHeader(row),
    alert: lastAttemptFailed ? DOCUMENT_PROCESSING_COPY.lastAttemptFailed : null,
    rows,
    warning: buildCompletenessWarning(completeness, unit, readAsChosen),
    // A recording has no pictures, so it has none to account for either.
    pictures: isRecording ? [] : buildPictureNotices(completeness, unit),
    renders: isRecording ? [] : buildRenderNotices(completeness.pageRenders),
    sheetsNotRead: buildSheetsNotReadNotices(completeness.coverage),
    note: readAsChosen ? buildChosenSpanNote(completeness, unit) : null,
    footer: buildFinishedFooter(processing, now),
    citations,
    sourceCheck,
    ...(readTheRest ? { readTheRest } : {}),
    readTheRestNote: readTheRest === "limit" ? DOCUMENT_PROCESSING_COPY.readAllPicturesNote : null,
    retryOffered: lastAttemptFailed,
    reprocessOffered: isReprocessOffered(row),
    picturesUnavailable: isDocumentPicturesUnavailable(processing),
  };
}

export type DocumentCompletenessFooterActionKind =
  | "try_again"
  | "reprocess"
  | "read_more"
  | "read_the_rest";

export type DocumentCompletenessFooterAction = {
  kind: DocumentCompletenessFooterActionKind;
  label: string;
  variant: "outline" | "default";
};

/**
 * The one action a completeness popover's footer offers, in precedence order.
 *
 * Try again wins outright — it is the newest fact, and the person is waiting on exactly that
 * press to land. Next, a read whose pictures cannot be opened (`picturesUnavailable`) offers
 * Reprocess ahead of either read-the-rest press: both of those replay or re-read the parts the
 * document already has, and neither restores a stored image that is simply gone, so leading with
 * them would repeat the one thing that cannot repair this read. Only once that shortfall is
 * ruled out do the two read-the-rest offers apply — they are cheaper and answer a more specific
 * question than a full re-read does. Last, a document whose parse is merely outdated (not
 * damaged) falls back to Reprocess as the final repair.
 *
 * A view-model function rather than a component branch, so the component stays a placer: it
 * only maps the returned `kind` to the callback the caller bound for it.
 */
export function resolveCompletenessFooterAction(
  payload: Pick<
    DocumentCompletenessPopover,
    "retryOffered" | "readTheRest" | "reprocessOffered" | "picturesUnavailable"
  >
): DocumentCompletenessFooterAction | null {
  if (payload.retryOffered) {
    return { kind: "try_again", label: DOCUMENT_PROCESSING_COPY.tryAgain, variant: "default" };
  }
  if (payload.reprocessOffered && payload.picturesUnavailable) {
    return { kind: "reprocess", label: DOCUMENT_PROCESSING_COPY.reprocess, variant: "outline" };
  }
  if (payload.readTheRest === "scope") {
    return { kind: "read_more", label: DOCUMENT_PROCESSING_COPY.readMore, variant: "outline" };
  }
  if (payload.readTheRest === "limit") {
    return {
      kind: "read_the_rest",
      label: DOCUMENT_PROCESSING_COPY.readAllPictures,
      variant: "outline",
    };
  }
  if (payload.reprocessOffered) {
    return { kind: "reprocess", label: DOCUMENT_PROCESSING_COPY.reprocess, variant: "outline" };
  }
  return null;
}

/**
 * Whether this finished read has anything to disclose.
 *
 * A whole read of a whole file is not a subject: the design's plain Ready row carries no
 * chevron, so the popover exists only where a span was chosen, a cap bit, the summary came out
 * degraded, or the newest attempt to replace this read failed.
 *
 * A finished *recording* is the one exception, and it is not really one: its length is a fact
 * about the file that nothing else on the row carries — a document's page count reaches a
 * person through the file itself, and a 42-minute meeting's does not.
 */
function hasCompletenessDisclosure(processing: DocumentProcessingFacts | undefined): boolean {
  const completeness = processing?.completeness;
  if (!completeness) return false;
  if (processing?.media !== undefined) return true;
  // A whole file read whole discloses nothing about itself — but a failed attempt to read it
  // again is still something a person pressed, and the panel is where the repair lives.
  if (processing?.lastReadAttemptFailed) return true;
  if (resolveReadTheRest(processing)) return true;
  if (completeness.coverage?.status === "partial") return true;
  // A worksheet left out is named only here: a workbook with a chart sheet is read whole and its
  // row says Ready, and nothing else says which of its sheets holds nothing a reader can see.
  if (describeSheetsNotRead(completeness.coverage).sentences.length > 0) return true;
  // Every drop is a subject, decoration included: the sentence explaining a logo read once is
  // only reachable through this panel, and a deck whose picture count is smaller than its slide
  // count is owed it.
  if (completeness.figures.found > completeness.figures.kept) return true;
  if ((completeness.figures.decorative ?? 0) > 0) return true;
  if ((completeness.pageRenders?.dropped ?? 0) > 0) return true;
  // A sheet too dense to read in one image was split into an overview and tiles, and this panel
  // is the only place that says so — which is what a reader asking "what can it actually see of
  // that sheet?" is asking about. A workbook whose sheets each fit one image says nothing new.
  if (completeness.pageRenders?.bySheet?.some((sheet) => !sheet.whole)) return true;
  // Every object this document carries is named only here: the row says nothing about them, and
  // the list behind this panel is the only way to reach a child or ask for one.
  if ((completeness.embeddedObjects?.total ?? 0) > 0) return true;
  // A worksheet the scan stopped short of may carry files nobody has seen, and finding none so
  // far is exactly when the offer to look further is the only thing this panel has to give.
  if ((completeness.embeddedObjects?.sheetsNotFullyInspected ?? 0) > 0) return true;
  if (
    completeness.descriptions !== undefined &&
    completeness.descriptions.described < completeness.descriptions.found
  ) {
    return true;
  }
  // A degraded card is a subject only where it has something to say: a summary of pages 1–55
  // is flagged degraded by the pipeline and is complete for what was read, and the coverage
  // above already earns this row its chevron.
  return completeness.card !== undefined && buildSummaryWarning(completeness.card) !== null;
}

type SpanUnit = { singular: string; plural: string; label: string; total?: number };

/**
 * Pages, Slides or Sheets — the first row of the read-out, and the word its sentences use.
 *
 * `total` is the one number every sentence below counts against, so a `0` is turned into absence
 * here rather than guarded at each of them: a span is 1-indexed, and a backend that sends zero is
 * a reader that defaulted an absence. Absent is never defaulted to zero, and it must never be
 * *read* as zero either.
 */
function resolveSpanUnit(
  processing: DocumentProcessingFacts | undefined,
  completeness: DocumentCompleteness
): SpanUnit {
  const format = processing?.probe?.format;
  if (format === "pptx" || completeness.slideCount !== undefined) {
    return {
      singular: "slide",
      plural: "slides",
      label: "Slides",
      total: presentSpanCount(completeness.slideCount),
    };
  }
  if (format === "xlsx" || completeness.sheetCount !== undefined) {
    return {
      singular: "sheet",
      plural: "sheets",
      label: "Sheets",
      total: presentSpanCount(completeness.sheetCount),
    };
  }
  return {
    singular: "page",
    plural: "pages",
    label: "Pages",
    total: presentSpanCount(completeness.pageCount),
  };
}

/**
 * The finished read's first row: "Pages read: 91 of 3,590", with the bar underneath where the
 * count is short of the whole file.
 *
 * The one row named after what it discloses rather than after the unit alone — a person reading
 * "Pages: 91 of 3,590" still has to work out whether that count is what was read or what the
 * file holds, and "Pages read" says which. The spans behind the count live in the note or the
 * warning below (`buildChosenSpanNote`, `buildCompletenessWarning`), which is the one place this
 * popover spells them out — this row states only how much.
 */
function buildSpanRow(
  completeness: DocumentCompleteness,
  unit: SpanUnit,
  readAsChosen: boolean
): DocumentFactRow | undefined {
  const coverage = completeness.coverage;
  const covered = coverage ? countCoveredSpan(coverage) : null;
  if (coverage?.status === "partial" && covered !== null && coverage.totalPages) {
    return {
      label: `${unit.label} read`,
      value: `${formatCount(covered)} of ${formatCount(coverage.totalPages)}`,
      // "55 of 3,590" is alarming in amber and merely factual in grey, and which one it is
      // depends entirely on whether 55 is the number somebody asked for.
      tone: readAsChosen ? "muted" : "warning",
      fraction: clampFraction(covered / coverage.totalPages),
    };
  }
  // A workbook's sheet count includes sheets its reading left out, such as a chart sheet; the
  // row says how many were read, and the notices below name the rest.
  const read = unit.singular === "sheet" ? (coverage?.sheets?.read ?? unit.total) : unit.total;
  if (read === undefined) return undefined;
  return { label: `${unit.label} read`, value: formatCount(read), tone: "default" };
}

function countCoveredSpan(coverage: NonNullable<DocumentCompleteness["coverage"]>): number | null {
  const ranges = coverage.coveredPageRanges;
  if (!ranges || ranges.length === 0) return null;
  return countPageRanges(ranges);
}

/** Headings, paragraphs, lists and code, as one number. `null` when nothing counted. */
function countTextSections(completeness: DocumentCompleteness): number | null {
  const blocks = completeness.blocksByType;
  if (!blocks) return null;
  let total = 0;
  let counted = false;
  for (const type of TEXT_SECTION_BLOCK_TYPES) {
    const value = blocks[type];
    if (value === undefined) continue;
    total += value;
    counted = true;
  }
  return counted ? total : null;
}

/**
 * "Pictures 58 of 58" — how many pictures this read holds, out of how many it could have.
 *
 * Decoration is never in the denominator: the same logo on every slide is one picture the reader
 * sets aside before any budget sees it, and counting each repeat as a picture the read is missing
 * turns a working deck into "58 of 72" in amber — a shortfall of fourteen things nobody wanted.
 * The reader keeps those out of `found`, and the row says the denominator out loud where it did,
 * so the count reads as this deck's whole picture set rather than as a number cut down to size.
 *
 * The tone is the loss, not the shortfall: a row is amber only where a picture carrying content
 * is missing, so a 16 × 16 bullet left behind reads in the same grey as the label beside it.
 */
function buildPicturesRow(completeness: DocumentCompleteness): DocumentFactRow {
  const { kept, found, decorative } = completeness.figures;
  if (found > kept) {
    return {
      label: "Pictures",
      value: `${formatCount(kept)} of ${formatCount(found)}`,
      tone: hasPictureContentLoss(completeness) ? "warning" : "default",
      fraction: clampFraction(kept / found),
    };
  }
  if ((decorative ?? 0) > 0) {
    return {
      label: "Pictures",
      value: `${formatCount(kept)} of ${formatCount(found)}`,
      tone: "default",
    };
  }
  return { label: "Pictures", value: formatCount(kept), tone: "default" };
}

/**
 * The neutral sentence: the spans somebody chose, and what they left out.
 *
 * Written only for a read that holds exactly what was asked for — a read that stopped short of
 * its own scope is a warning and says so through `buildCompletenessWarning`.
 */
function buildChosenSpanNote(completeness: DocumentCompleteness, unit: SpanUnit): string | null {
  const coverage = completeness.coverage;
  if (coverage?.status !== "partial") return null;
  const ranges = coverage.coveredPageRanges;
  const covered = countCoveredSpan(coverage);
  if (ranges?.length && covered !== null && coverage.totalPages) {
    const unread = Math.max(0, coverage.totalPages - covered);
    return `Read ${unit.plural} ${formatPageRangesProse(ranges)} only, as you chose. The other ${formatCount(unread)} ${unread === 1 ? unit.singular : unit.plural} were not read.`;
  }
  return "Only part of this file was read, as you chose. The rest was not.";
}

/**
 * The one sentence about something that went wrong: a read that stopped short of its own
 * scope, the cap that cut the pictures, or a summary that could not be written.
 *
 * The span comes first when a read stopped short, for the same reason `resolveReadTheRest`
 * orders them that way — completing the span re-reads the file under today's caps anyway. A
 * span somebody *chose* is not in this list at all: it is the popover's note.
 */
function buildCompletenessWarning(
  completeness: DocumentCompleteness,
  unit: SpanUnit,
  readAsChosen: boolean
): string | null {
  const coverage = completeness.coverage;
  if (coverage?.status === "partial" && !readAsChosen) {
    const ranges = coverage.coveredPageRanges;
    const covered = countCoveredSpan(coverage);
    if (ranges?.length && covered !== null && coverage.totalPages) {
      const unread = Math.max(0, coverage.totalPages - covered);
      return `Reading stopped after ${unit.plural} ${formatPageRangesProse(ranges)}. The other ${formatCount(unread)} ${unread === 1 ? unit.singular : unit.plural} were not read.`;
    }
    return "Only part of this file was read; the rest was not.";
  }

  const pictures = buildPictureLimitSentence(completeness, unit);
  if (pictures) return pictures;

  const renders = completeness.pageRenders;
  // A workbook's own views are named sheet by sheet below, each loss with the sheet and the
  // range it cost: "the first 20 page photos" counts pages a workbook does not have.
  if (renders && renders.dropped > 0 && !hasSheetRenders(renders)) {
    return `We took the first ${formatCount(renders.rendered)} page photos — the limit for one document — and ${formatCount(renders.dropped)} were skipped.`;
  }

  const undescribed = buildDescriptionWarning(completeness.descriptions);
  if (undescribed) return undescribed;

  return completeness.card ? buildSummaryWarning(completeness.card) : null;
}

/**
 * Why some of this document's pictures carry no description.
 *
 * Every shortfall names its own cause, because an amber "42 of 85" alone cannot say whether it
 * is a limit somebody chose, a fault, or a run still going. They share one consequence — a
 * picture nobody described is a picture that answers no search and reaches no reader.
 */
function buildDescriptionWarning(
  descriptions: DocumentDescriptionCompleteness | undefined
): string | null {
  if (!descriptions) return null;
  const missing = descriptions.found - descriptions.described;
  if (missing <= 0) return null;
  const noun = missing === 1 ? "picture" : "pictures";
  const tail = `Their content is missing from search and from what Claudia reads.`;
  if (descriptions.reasonKind === "image_unavailable") {
    return `${formatCount(missing)} of the ${formatCount(descriptions.found)} pictures could not be opened for describing. ${tail} ${DOCUMENT_PROCESSING_COPY.reprocessToReadPictures}`;
  }
  return `We could not describe ${formatCount(missing)} of the ${formatCount(descriptions.found)} ${noun}. ${tail}`;
}

/**
 * What became of the pictures this read did not keep — which cap cut them, in that cap's own
 * words, and how many no cap could have saved.
 *
 * Two different losses with two different endings, so the sentence is built from the *causes* and
 * not from the presence of a limit. A cap is a budget somebody can raise, and its sentence is the
 * one "Read all pictures" belongs to; a picture the reader could not extract at all — a corrupt
 * source, an upload that failed — is lost at any budget, so it is stated plainly and offered
 * nothing. Both can be true of one document, and then both are said, over counts that add up to
 * the shortfall the row above shows.
 *
 * The cap's own number is the **cap**, never the count kept: printing the kept count as the
 * limit offers to raise a budget the read never came near.
 *
 * A revision whose parse attributed nothing (`unextracted` absent) has its whole shortfall
 * treated as the cap's.
 *
 * This is the reading for a revision whose reader named no reasons. Where one named them all
 * (`figures.dropped`), the panel says which pictures and why through `buildPictureNotices`
 * instead, and there is nothing left here for a cap to guess at. Where one named only some, the
 * remainder it left unattributed is exactly the part no notice accounts for, and it is said here
 * in the same words a wholly unattributed shortfall is.
 */
function buildPictureLimitSentence(
  completeness: DocumentCompleteness,
  unit: SpanUnit
): string | null {
  const { kept, found, unextracted, dropped: attributed } = completeness.figures;
  if (attributed) {
    const unknown = unattributedDrops(attributed);
    return unknown > 0 ? buildCapSentence(completeness, unit, unknown) : null;
  }
  const dropped = found - kept;
  if (dropped <= 0) return null;
  const cappedCount = unextracted === undefined ? dropped : Math.max(0, dropped - unextracted);
  const sentences = [
    buildCapSentence(completeness, unit, cappedCount),
    buildUnextractedSentence(unextracted ?? 0),
  ].filter((sentence): sentence is string => sentence !== null);
  return sentences.length > 0 ? sentences.join(" ") : null;
}

/** The cap's sentence, in the words of the constraint that actually bound. */
function buildCapSentence(
  completeness: DocumentCompleteness,
  unit: SpanUnit,
  cappedCount: number
): string | null {
  // No picture went to a cap, so no cap has anything to answer for — and the backend has already
  // withheld the repair this sentence would otherwise announce.
  if (cappedCount <= 0) return null;
  const skipped = formatCount(cappedCount);
  const limit = completeness.figures.limit;

  if (limit?.kind === "chunk_slots") {
    return `We kept the first ${formatCount(limit.value)} pictures of this part — the reader's per-part allowance — and ${skipped} were skipped. ${DOCUMENT_PROCESSING_COPY.readAllPictures} will collect them.`;
  }
  if (limit?.kind === "worker_ceiling") {
    return `This reader allows at most ${formatCount(limit.value)} pictures per document; ${skipped} were skipped.`;
  }
  // Only a read that covers the whole file may claim it did: appending "All 8 slides were
  // read" to a read of slides 5–12 would contradict the row above it.
  const whole =
    completeness.coverage?.status !== "partial" && unit.total !== undefined
      ? ` All ${formatCount(unit.total)} ${unit.plural} were read.`
      : "";
  // A row too old to name its limit states what happened without naming a number for it: the
  // count kept is not the cap, and printing it as one is the lie this rule exists to stop.
  if (limit === undefined) {
    return `We read the first ${formatCount(completeness.figures.kept)} pictures and ${skipped} were skipped.${whole}`;
  }
  return `We read the first ${formatCount(limit.value)} pictures — the limit for one document — and ${skipped} were skipped.${whole}`;
}

/**
 * The pictures no budget would have saved: the reader had them and could not extract them.
 *
 * Stated without a repair on purpose — re-reading the file hands the same bytes to the same
 * reader — which is the whole reason it is not folded into the cap's sentence above.
 */
function buildUnextractedSentence(unextracted: number): string | null {
  if (unextracted <= 0) return null;
  const noun = unextracted === 1 ? "picture" : "pictures";
  return `${formatCount(unextracted)} ${noun} could not be extracted from this file.`;
}

/* -------------------------------------------------------------------------- */
/* Which pictures were left out, and why                                      */
/* -------------------------------------------------------------------------- */

/**
 * One sentence per reason group, saying which pictures are missing and what — if anything —
 * puts them back.
 *
 * "14 pictures could not be extracted from this file" answers none of the three questions a
 * person actually has, so each group names what the reader knew about its own drops: the format
 * it could not decode, the pixels that were too few to carry anything, the upload that failed.
 * The order is the order they are worth acting on — the recoverable loss first, the decoration
 * last — and the tone separates a loss from a fact about the file.
 *
 * A group whose `detail` entries do not account for every one of its drops is described by its
 * count alone: the detail is a bounded sample, and "none larger than 40 × 40" read off a sample
 * is a claim about pictures nobody looked at.
 */
function buildPictureNotices(
  completeness: DocumentCompleteness,
  unit: SpanUnit
): DocumentPictureNotice[] {
  const figures = completeness.figures;
  const notices: DocumentPictureNotice[] = [];
  const drops = figures.dropped;
  if (drops) {
    const notStored = countDrops(drops, PICTURE_RECOVERABLE_DROP_REASONS);
    if (notStored > 0) notices.push({ text: buildNotStoredSentence(notStored), tone: "warning" });

    const undecodable = drops.byReason.undecodable ?? 0;
    if (undecodable > 0) {
      const text = buildUndecodableSentence(undecodable, dropDetail(drops, ["undecodable"]));
      notices.push({ text, tone: "warning" });
    }

    const linked = drops.byReason.linked ?? 0;
    if (linked > 0) notices.push({ text: buildLinkedSentence(linked), tone: "warning" });

    const glyphs = countDrops(drops, PICTURE_GLYPH_DROP_REASONS);
    if (glyphs > 0) {
      const text = buildTooSmallSentence(glyphs, dropDetail(drops, PICTURE_GLYPH_DROP_REASONS));
      notices.push({ text, tone: "muted" });
    }
  }
  const decorative = figures.decorative ?? 0;
  if (decorative > 0)
    notices.push({ text: buildDecorativeSentence(decorative, unit), tone: "muted" });
  return notices;
}

function dropDetail(
  drops: DocumentFigureDrops,
  reasons: readonly FigureDropReason[]
): FigureDropDetail[] {
  const wanted = new Set<string>(reasons);
  return (drops.detail ?? []).filter((entry) => wanted.has(entry.reason));
}

/** The recoverable loss: the reader had the picture and it did not reach storage. */
function buildNotStoredSentence(count: number): string {
  return count === 1
    ? "1 picture was not stored; Reprocess reads it again."
    : `${formatCount(count)} pictures were not stored; Reprocess reads them again.`;
}

/**
 * The formats this reader cannot draw, named — because the repair is in the file, not here.
 *
 * Saving the deck with its vector icons as PNG is something a person can do in the ten seconds
 * after reading this, and it is the only thing that makes those pictures readable at all.
 */
function buildUndecodableSentence(count: number, detail: FigureDropDetail[]): string {
  const tail =
    count === 1
      ? "could not be decoded by this reader; it shows as a placeholder. Saving the file with that picture as PNG makes it readable."
      : "could not be decoded by this reader; they show as placeholders. Saving the file with those pictures as PNG makes them readable.";
  const groups = groupDropFormats(count, detail);
  if (!groups) {
    return `${formatCount(count)} ${count === 1 ? "picture" : "pictures"} ${tail}`;
  }
  const phrases = groups.map(
    (group) =>
      `${formatCount(group.count)} ${group.label} ${pictureNounFor(group.key, group.count)}`
  );
  return `${joinPhrases(phrases)} ${tail}`;
}

function buildLinkedSentence(count: number): string {
  return count === 1
    ? "1 picture is a link to a file outside the document and was not fetched."
    : `${formatCount(count)} pictures are links to files outside the document and were not fetched.`;
}

/**
 * The glyphs: a bullet, a rule, a line of a signature. Stated once so the count above adds up,
 * and stated in grey because nothing was lost and nothing can be done.
 */
function buildTooSmallSentence(count: number, detail: FigureDropDetail[]): string {
  const noun = count === 1 ? "image" : "images";
  const tail = `${count === 1 ? "was" : "were"} left out as too small to carry content.`;
  const measure = describeDropSize(count, detail);
  if (!measure) return `${formatCount(count)} ${noun} ${tail}`;
  if (count === 1 || measure.uniform) {
    return `${formatCount(count)} ${noun} of ${measure.label} ${tail}`;
  }
  return `${formatCount(count)} ${noun}, none larger than ${measure.label}, ${tail}`;
}

/** The repeat rule, as the fact it is: the file says this picture 60 times and means it once. */
function buildDecorativeSentence(count: number, unit: SpanUnit): string {
  return count === 1
    ? `The same picture repeated on another ${unit.singular} was read once.`
    : `The same picture repeated on ${formatCount(count)} ${unit.plural} was read once.`;
}

type DropFormatGroup = { key: string; label: string; count: number };

/**
 * The dropped formats, largest group first — `null` where the detail cannot speak for every
 * drop in the group or a single entry names no format at all.
 */
function groupDropFormats(count: number, detail: FigureDropDetail[]): DropFormatGroup[] | null {
  if (detail.length !== count || count === 0) return null;
  const counts = new Map<string, number>();
  for (const entry of detail) {
    const key = normalizeDropFormat(entry.format);
    if (!key) return null;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([key, groupCount]) => ({ key, label: formatDropFormatLabel(key), count: groupCount }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function normalizeDropFormat(format: string | undefined): string | null {
  if (!format) return null;
  const trimmed = format
    .trim()
    .toLowerCase()
    .replace(/^image\//, "")
    .replace(/^\./, "");
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * How a picture format is spelled to a person. Anything unlisted is upper-cased, which is right
 * for every three-letter extension a reader is likely to meet and wrong for nothing that matters.
 */
const DROP_FORMAT_LABELS: Record<string, string> = {
  avif: "AVIF",
  bmp: "BMP",
  emf: "EMF",
  eps: "EPS",
  gif: "GIF",
  heic: "HEIC",
  heif: "HEIF",
  ico: "ICO",
  jpeg: "JPEG",
  jpg: "JPEG",
  jpegxr: "JPEG XR",
  jxr: "JPEG XR",
  pict: "PICT",
  png: "PNG",
  svg: "SVG",
  "svg+xml": "SVG",
  tif: "TIFF",
  tiff: "TIFF",
  wdp: "JPEG XR",
  webp: "WebP",
  wmf: "WMF",
};

function formatDropFormatLabel(key: string): string {
  return DROP_FORMAT_LABELS[key] ?? key.toUpperCase();
}

/** Vector formats carry line art; everything else carries a picture of something. */
const VECTOR_DROP_FORMATS = new Set(["svg", "svg+xml", "emf", "wmf", "eps"]);

function pictureNounFor(formatKey: string, count: number): string {
  const singular = VECTOR_DROP_FORMATS.has(formatKey) ? "icon" : "photo";
  return count === 1 ? singular : `${singular}s`;
}

/** "a, b and c" — the list separator this panel uses everywhere it names groups. */
function joinPhrases(phrases: string[]): string {
  if (phrases.length <= 1) return phrases[0] ?? "";
  return `${phrases.slice(0, -1).join(", ")} and ${phrases[phrases.length - 1]}`;
}

type DropSizeBound = { label: string; uniform: boolean };

/**
 * How big the dropped glyphs were — pixels where the reader measured them, bytes where it only
 * weighed them, and nothing at all where the detail cannot speak for every drop in the group.
 */
function describeDropSize(count: number, detail: FigureDropDetail[]): DropSizeBound | null {
  if (detail.length !== count || count === 0) return null;
  const sized = detail.filter(
    (entry): entry is FigureDropDetail & { width: number; height: number } =>
      entry.width !== undefined && entry.height !== undefined
  );
  if (sized.length === count) {
    const largest = sized.reduce((max, entry) =>
      entry.width * entry.height > max.width * max.height ? entry : max
    );
    return {
      label: `${formatCount(largest.width)} × ${formatCount(largest.height)} pixels`,
      uniform: sized.every(
        (entry) => entry.width === largest.width && entry.height === largest.height
      ),
    };
  }
  const weighed = detail.filter(
    (entry): entry is FigureDropDetail & { bytes: number } => entry.bytes !== undefined
  );
  if (weighed.length !== count) return null;
  const heaviest = weighed.reduce((max, entry) => (entry.bytes > max.bytes ? entry : max));
  const label = formatFileSize(heaviest.bytes);
  if (label === null) return null;
  return { label, uniform: weighed.every((entry) => entry.bytes === heaviest.bytes) };
}

/**
 * Why a summary is short, in wording written for a person.
 *
 * `generation_failed` gets a fixed sentence rather than the pipeline's `reason`, which on that
 * path is a caught exception's message: a panel that printed it would be showing a stack-trace
 * fragment where it promised an explanation.
 */
/* -------------------------------------------------------------------------- */
/* Which views of a sheet this read has, and which it lost                    */
/* -------------------------------------------------------------------------- */

/**
 * Whether this document's renders are views of *sheets* rather than pages or slides.
 *
 * A sheet too dense to read in one image is rendered as an overview plus cell-range tiles, and
 * `bySheet` is the only thing that can say which sheet got which: "four rendered, three
 * dropped" is one dense sheet split three ways or four ordinary sheets, and the two want
 * different sentences.
 */
function hasSheetRenders(renders: DocumentPageRenderCompleteness): boolean {
  return (renders.bySheet?.length ?? 0) > 0;
}

/**
 * The one drop reason that costs no content: the sheet is too wide for a single fitted page to
 * hold at all, so it ships as tiles with nothing showing it whole. Everything the sheet says is
 * in the tiles, and no re-read produces the overview, so it is neither a shortfall to repair
 * nor a loss to flag.
 */
const RENDER_LAYOUT_ONLY_REASON: PageRenderDropReason = "overview_too_wide";

/** Views this read is actually short of — every drop but the one that costs no content. */
function countRenderContentLoss(renders: DocumentPageRenderCompleteness | undefined): number {
  if (!renders) return 0;
  const layoutOnly = renders.droppedByReason?.[RENDER_LAYOUT_ONLY_REASON] ?? 0;
  return Math.max(0, renders.dropped - layoutOnly);
}

/**
 * One row per sheet — what this read has a picture of — or the page count for a document whose
 * renders are pages or slides.
 *
 * A sheet's row names the views rather than counting images, because the views are what a
 * reader can ask for: an overview is for layout and the tiles are what the text is legible in.
 */
function buildRenderRows(renders: DocumentPageRenderCompleteness): DocumentFactRow[] {
  const bySheet = renders.bySheet;
  if (!bySheet || bySheet.length === 0) {
    const total = renders.rendered + renders.dropped;
    return [
      {
        label: "Page photos",
        value: `${formatCount(renders.rendered)} of ${formatCount(total)}`,
        tone: renders.dropped > 0 ? "warning" : "default",
      },
    ];
  }
  const lostSheets = sheetsWithLostViews(renders.drops);
  const total = renders.rendered + renders.dropped;
  // The count the descriptions row is partly over. Without it the panel read "Pictures 7"
  // beside "Descriptions 11 of 11" — the four views the sheets below account for one by one
  // were in the denominator and in no count above it, so the two lines contradicted each other.
  const viewsRow: DocumentFactRow = {
    label: "Views",
    value:
      renders.dropped > 0
        ? `${formatCount(renders.rendered)} of ${formatCount(total)}`
        : formatCount(renders.rendered),
    tone: lostSheets.size > 0 ? "warning" : "default",
  };
  return [
    ...(total > 0 ? [viewsRow] : []),
    ...bySheet.map((sheet) => ({
      label: sheet.sheetName,
      value: formatSheetRenderValue(sheet, renders.drops),
      tone: lostSheets.has(sheet.sheetName) ? ("warning" as const) : ("default" as const),
    })),
  ];
}

/** "Rendered", "Rendered: overview + 3 tiles", "Rendered: 3 tiles (overview too wide)". */
function formatSheetRenderValue(
  sheet: NonNullable<DocumentPageRenderCompleteness["bySheet"]>[number],
  drops: readonly PageRenderDropDetail[] | undefined
): string {
  if (sheet.whole) return "Rendered";
  const parts: string[] = [];
  if (sheet.overview) parts.push("overview");
  if (sheet.tiles > 0)
    parts.push(`${formatCount(sheet.tiles)} ${sheet.tiles === 1 ? "tile" : "tiles"}`);
  if (parts.length === 0) return "Not rendered";
  const tooWide =
    !sheet.overview &&
    (drops ?? []).some(
      (drop) =>
        drop.reason === RENDER_LAYOUT_ONLY_REASON && drop.render?.sheetName === sheet.sheetName
    );
  return `Rendered: ${parts.join(" + ")}${tooWide ? " (overview too wide)" : ""}`;
}

/** The sheets a view was actually lost on — the layout-only drop is not one. */
function sheetsWithLostViews(drops: readonly PageRenderDropDetail[] | undefined): Set<string> {
  const sheets = new Set<string>();
  for (const drop of drops ?? []) {
    if (drop.reason === RENDER_LAYOUT_ONLY_REASON) continue;
    if (drop.render?.sheetName) sheets.add(drop.render.sheetName);
  }
  return sheets;
}

/** How each reason says what it cost, over the subject the drops themselves name. */
const RENDER_LOSS_SENTENCES: Readonly<
  Record<
    Exclude<PageRenderDropReason, "overview_too_wide">,
    (subject: string, was: string) => string
  >
> = {
  render_cap: (subject, was) =>
    `The workbook's render budget ran out; ${subject} ${was} not rendered.`,
  byte_budget: (subject, was) => `The image budget ran out; ${subject} ${was} not rendered.`,
  time_budget: (subject, was) => `The render step ran out of time; ${subject} ${was} not rendered.`,
  render_failed: (subject, was) => `${subject} ${was} not rendered: the renderer produced nothing.`,
};

/**
 * A skipped chart sheet reads grey; any sheet whose cells were not read reads as a warning. The
 * two groups are described separately so each sentence keeps its own tone, and the wording stays
 * the shared one Claudia uses.
 */
function buildSheetsNotReadNotices(
  coverage: DocumentCompleteness["coverage"]
): DocumentPictureNotice[] {
  const sheets = coverage?.sheets;
  if (!coverage || !sheets) return [];
  const notRead = sheets.notRead ?? [];
  const charts = notRead.filter((sheet) => sheet.reason === "chart_sheet");
  const lost = notRead.filter((sheet) => sheet.reason !== "chart_sheet");
  const describe = (group: typeof notRead, omitted: number | undefined) =>
    describeSheetsNotRead({
      ...coverage,
      sheets: { ...sheets, notRead: group, notReadOmitted: omitted },
    }).sentences;
  return [
    ...describe(charts, 0).map((text) => ({ text, tone: "muted" as const })),
    ...describe(lost, sheets.notReadOmitted).map((text) => ({ text, tone: "warning" as const })),
  ];
}

/**
 * One sentence per reason, over the views it cost.
 *
 * The counts are complete and the per-view detail is bounded, so a reason whose detail does not
 * account for all of its drops is stated by its count alone — the same rule the picture notices
 * follow, and for the same reason: "the last 3 tiles of Sheet2" read off a sample is a claim
 * about views nobody looked at.
 */
function buildRenderNotices(
  renders: DocumentPageRenderCompleteness | undefined
): DocumentPictureNotice[] {
  if (!renders || !hasSheetRenders(renders)) return [];
  const notices: DocumentPictureNotice[] = [];
  const byReason = renders.droppedByReason ?? {};
  for (const [reason, build] of Object.entries(RENDER_LOSS_SENTENCES)) {
    const count = byReason[reason as PageRenderDropReason] ?? 0;
    if (count <= 0) continue;
    const detail = (renders.drops ?? []).filter((drop) => drop.reason === reason);
    const groups = detail.length === count ? groupRenderDrops(detail) : null;
    if (groups === null) {
      const subject = `${formatCount(count)} ${count === 1 ? "view" : "views"}`;
      notices.push({ text: build(subject, count === 1 ? "was" : "were"), tone: "warning" });
      continue;
    }
    for (const group of groups) {
      notices.push({ text: build(group.subject, group.was), tone: "warning" });
    }
  }
  for (const drop of renders.drops ?? []) {
    if (drop.reason !== RENDER_LAYOUT_ONLY_REASON) continue;
    const sheetName = drop.render?.sheetName;
    notices.push({
      // Nothing is missing: every cell the sheet holds is in the tiles, and no re-read makes a
      // fitted page of a sheet too wide for one. So it is stated, not flagged.
      text: sheetName
        ? `"${sheetName}" is too wide for one image to hold whole, so it was rendered as tiles only.`
        : "One sheet is too wide for one image to hold whole, so it was rendered as tiles only.",
      tone: "muted",
    });
  }
  return notices;
}

type RenderDropGroup = { subject: string; was: string };

/**
 * The views one reason cost, gathered per sheet.
 *
 * A group of exactly one is named in full — its range is the fact worth having, and
 * `formatRenderLabel` is the one place a view becomes words. Several are counted instead: five
 * cell ranges in a row is a list, not a sentence.
 */
function groupRenderDrops(detail: readonly PageRenderDropDetail[]): RenderDropGroup[] {
  const bySheet = new Map<string, PageRenderDropDetail[]>();
  for (const drop of detail) {
    const key = drop.render?.sheetName ?? drop.label;
    const existing = bySheet.get(key);
    if (existing) existing.push(drop);
    else bySheet.set(key, [drop]);
  }
  return [...bySheet.entries()].map(([sheetName, drops]) => {
    const only = drops[0];
    if (drops.length === 1 && only) {
      const label = only.render ? formatRenderLabel(only.render) : only.label;
      return { subject: label, was: "was" };
    }
    const tiles = drops.filter((drop) => drop.render?.kind === "sheet_tile").length;
    const overviews = drops.length - tiles;
    const phrases: string[] = [];
    if (overviews > 0)
      phrases.push(overviews === 1 ? "the overview" : `${formatCount(overviews)} overviews`);
    if (tiles > 0) {
      phrases.push(`the last ${formatCount(tiles)} ${tiles === 1 ? "tile" : "tiles"}`);
    }
    return { subject: `${joinPhrases(phrases)} of "${sheetName}"`, was: "were" };
  });
}

/* -------------------------------------------------------------------------- */
/* The files a document carries inside itself                                 */
/* -------------------------------------------------------------------------- */

/**
 * Where a child document came from, said at the top of its own status panel.
 *
 * `text` is the whole sentence and the three parts are the same sentence split where the parent
 * document's name sits, because that name is the way to its row. They are one fact in two
 * shapes, never two wordings: `text` is `prefix + parentName + suffix`.
 */
export type DocumentEmbeddedHeader = {
  text: string;
  prefix: string;
  parentName: string;
  parentArtifactId: string;
  suffix: string;
};

/**
 * The parent this child's panel names — the one that still carries it, and only failing that one
 * that no longer does, which is the same choice the row's own provenance line makes.
 */
function buildEmbeddedHeader(row: DocumentStatusSource): DocumentEmbeddedHeader | null {
  const provenance = primaryEmbeddedProvenance(row.embeddedIn ?? []);
  if (!provenance) return null;
  const prefix =
    provenance.supersededAt === undefined
      ? DOCUMENT_PROCESSING_COPY.embeddedInPrefix
      : DOCUMENT_PROCESSING_COPY.embeddedNoLongerInPrefix;
  const where = provenance.supersededAt === undefined ? formatEmbeddedWhere(provenance) : undefined;
  const suffix = where
    ? ` ${where.preposition} ${where.locator}${where.detail ? `, ${where.detail}` : ""}`
    : "";
  return {
    text: `${prefix}${provenance.parentName}${suffix}`,
    prefix,
    parentName: provenance.parentName,
    parentArtifactId: provenance.parentArtifactId,
    suffix,
  };
}

/**
 * `19`, or `19 (18 extracted, 1 not extracted)`.
 *
 * Extraction is the only thing this count knows: it is what the parse recorded about the
 * parent's bytes, and a child that was extracted may be queued, failing or gone. Whether a
 * child is readable is a fact about that child, said per object and never summed here.
 *
 * The parenthesis is drawn only where there is a shortfall: a workbook whose every icon became
 * a document has nothing to account for, and "(19 extracted, 0 not extracted)" would put a
 * reckoning on a complete read.
 */
function formatEmbeddedCounts(embedded: DocumentEmbeddedObjectsCompleteness): string {
  const missing = Math.max(0, embedded.total - embedded.extracted);
  if (missing === 0) return formatCount(embedded.total);
  return `${formatCount(embedded.total)} (${formatCount(embedded.extracted)} extracted, ${formatCount(missing)} not extracted)`;
}

/**
 * The one press an object can be offered, in the words of what it does.
 *
 * `extract_again` is not a retry: the child it made was deleted by a person, and asking again
 * makes a fresh one while the old entry stays in the inventory as what happened to it.
 */
export type DocumentEmbeddedObjectAction = {
  kind: "extract" | "extract_again";
  label: string;
};

/** One object of the parent's inventory, as the panel draws it. */
export type DocumentEmbeddedObjectView = {
  occurrenceId: string;
  name: string;
  /** `Sheet2!P32`, or `null` where the icon sat on no anchor cell. */
  locator: string | null;
  /** What the parent's bytes yielded — `null` where the extraction state says nothing useful. */
  state: string | null;
  /**
   * What the child document is doing now, or `null` where the object has no child to speak for.
   *
   * A second clause rather than a replacement for `state`: "extracted" is a fact about the
   * parent and "being read" is a fact about the child, and the panel's job is to stop the first
   * from being read as the second.
   */
  childState: string | null;
  /**
   * The muted line under the object's name: `Sheet2!P32 · extracted · being read`, or whichever
   * of the three the object has. `null` for an object that has none of them.
   *
   * Composed here rather than in the panel for the reason the rest of this module exists: a
   * separator between two facts is a wording decision, and one made in JSX is untestable.
   */
  detail: string | null;
  /**
   * How loudly the state is said. `warning` is content that is not here and no press will
   * bring: a linked object has no bytes in the file, an unsupported one cannot be opened, one
   * past the upload ceiling never fits. `muted` is everything a press or a wait still answers.
   */
  tone: DocumentStatusCellTone;
  /** The child's own row, when there is one to open. */
  childArtifactId: string | null;
  action: DocumentEmbeddedObjectAction | null;
};

/** A worksheet whose collection of icons was not read to the end, and the press that continues. */
export type DocumentEmbeddedSheetNotice = {
  sheetName: string;
  text: string;
  actionLabel: string;
};

export type DocumentEmbeddedObjectsSection = {
  objects: DocumentEmbeddedObjectView[];
  sheets: DocumentEmbeddedSheetNotice[];
  /** Said where the inventory holds more than this panel lists. `null` where it lists them all. */
  moreNote: string | null;
  /**
   * Whether the section has anything to draw at all.
   *
   * A worksheet whose icons were not inspected to the end is exactly the case where nothing was
   * found and the offer to look further matters most, so "no objects" cannot be the test — but a
   * capture with no objects and nothing left to inspect must not draw a heading over nothing.
   */
  hasContent: boolean;
};

/** What the parent's popover draws under EMBEDDED FILES, from the inventory it asked for. */
export function buildEmbeddedObjectsSection(
  inventory: {
    objects: readonly EmbeddedObjectLine[];
    more: boolean;
    sheetsNotFullyInspected: readonly string[];
  },
  /** Every object this capture's inventory names, from the row's own counts. */
  total: number | undefined
): DocumentEmbeddedObjectsSection {
  const objects = inventory.objects.map(buildEmbeddedObjectView);
  const sheets = inventory.sheetsNotFullyInspected.map((sheetName) => ({
    sheetName,
    text: `"${sheetName}" was not inspected to the end, so it may carry files nobody has seen yet.`,
    actionLabel: DOCUMENT_PROCESSING_COPY.embeddedInspect,
  }));
  const moreNote = buildEmbeddedMoreNote(inventory.objects.length, inventory.more, total);
  return {
    objects,
    sheets,
    moreNote,
    hasContent: objects.length > 0 || sheets.length > 0 || moreNote !== null,
  };
}

/**
 * "This panel lists the first 50. 21 more were found." — the count coming from the row's own
 * totals, which are complete where the list is bounded.
 *
 * A total the parse did not record leaves the sentence without a number rather than with a
 * guessed one: the panel's job here is to say that the list is not the whole inventory.
 */
function buildEmbeddedMoreNote(
  listed: number,
  more: boolean,
  total: number | undefined
): string | null {
  if (!more) return null;
  const remaining = total === undefined ? 0 : total - listed;
  if (remaining <= 0) return "More files are listed than this panel shows.";
  return `This panel lists the first ${formatCount(listed)}. ${formatCount(remaining)} more are listed.`;
}

function buildEmbeddedObjectView(line: EmbeddedObjectLine): DocumentEmbeddedObjectView {
  const state = resolveEmbeddedObjectState(line);
  const childState = resolveEmbeddedChildState(line);
  const locator = line.locator ?? null;
  // A child that has no row of its own yet cannot be opened, and a name that links to a row that
  // does not exist is a press that does nothing and explains nothing.
  const childArtifactId = isEmbeddedChildPlaced(line) ? (line.childArtifactId ?? null) : null;
  return {
    occurrenceId: line.occurrenceId,
    name: line.displayName,
    locator,
    state,
    childState,
    detail: [locator, state, childState].filter((part) => part !== null).join(" · ") || null,
    tone: resolveEmbeddedObjectTone(line),
    childArtifactId,
    action: resolveEmbeddedObjectAction(line),
  };
}

/**
 * Whether the child is a document of its own that a person can open.
 *
 * A child is registered before it is published, so an extraction that registered and did not
 * place leaves an occurrence naming an artifact with no row. `childStatus` is what says which of
 * the two this is; an inventory that states none is read as placed, which is what an occurrence
 * carrying a child id has always meant.
 */
const EMBEDDED_PLACED_CHILD_STATUSES = new Set(["queued", "processing", "ready", "failed"]);

function isEmbeddedChildPlaced(line: EmbeddedObjectLine): boolean {
  if (line.childArtifactId === undefined) return false;
  if (line.childStatus === undefined) return true;
  return EMBEDDED_PLACED_CHILD_STATUSES.has(line.childStatus);
}

/** The child's own current state, in a person's words, or `null` where it has no child. */
function resolveEmbeddedChildState(line: EmbeddedObjectLine): string | null {
  const copy = DOCUMENT_PROCESSING_COPY;
  switch (line.childStatus) {
    case "queued":
      return copy.embeddedChildQueued;
    case "processing":
      return copy.embeddedChildProcessing;
    case "ready":
      return copy.embeddedChildReady;
    case "failed":
      return line.childFailureReason
        ? `${copy.embeddedChildFailed}: ${line.childFailureReason}`
        : copy.embeddedChildFailed;
    case "deleted":
      return copy.embeddedChildDeleted;
    case "unplaced":
      return copy.embeddedChildUnplaced;
    default:
      return null;
  }
}

/**
 * What became of one object, in a person's words.
 *
 * The contract spells a refused object `listed:<reason>`, which is the wire's vocabulary and not
 * a sentence; the reason behind it already is one, and it is the one this says.
 */
function resolveEmbeddedObjectState(line: EmbeddedObjectLine): string | null {
  if (line.state === "extracted") return DOCUMENT_PROCESSING_COPY.embeddedExtracted;
  if (line.state === "deleted") return DOCUMENT_PROCESSING_COPY.embeddedDeleted;
  if (line.state === "pending") return DOCUMENT_PROCESSING_COPY.embeddedPending;
  if (line.state === "abandoned") return DOCUMENT_PROCESSING_COPY.embeddedAbandoned;
  if (line.state === "absent") return DOCUMENT_PROCESSING_COPY.embeddedAbsent;
  return line.listedReason
    ? `not extracted: ${describeEmbeddedObjectListedReason(line.listedReason)}`
    : "not extracted";
}

/**
 * The three refusals no press lifts — there are no bytes in the file, this reader cannot open
 * them, or the payload is past the upload ceiling — are the only ones said as a loss. A depth,
 * a budget or a read scope is an allowance, and the press to extend it is on the same line.
 */
function resolveEmbeddedObjectTone(line: EmbeddedObjectLine): DocumentStatusCellTone {
  if (line.state === "abandoned") return "warning";
  if (line.state !== "listed") return "muted";
  if (line.listedReason === "upload_failed") return "warning";
  return isEmbeddedRefusalPermanent(line.listedReason) ? "warning" : "muted";
}

function resolveEmbeddedObjectAction(
  line: EmbeddedObjectLine
): DocumentEmbeddedObjectAction | null {
  if (line.offer === "extract") {
    return { kind: "extract", label: DOCUMENT_PROCESSING_COPY.embeddedExtract };
  }
  if (line.offer === "extract_again") {
    return { kind: "extract_again", label: DOCUMENT_PROCESSING_COPY.embeddedExtractAgain };
  }
  return null;
}

function buildSummaryWarning(card: DocumentCardCompleteness): string | null {
  // A card nobody flagged has nothing to explain, whatever a stray reason string says.
  if (!card.degraded) return null;
  const reasonKind = readCardReasonKind(card);
  if (reasonKind === "empty_source_revision") {
    return "There was nothing readable in this file to summarise.";
  }
  if (reasonKind === "generation_failed") {
    return "The summary could not be written. Try again re-runs it.";
  }
  if (reasonKind !== undefined) return null;
  return "The summary was written from less than the whole document.";
}

/**
 * "Finished 4 min ago · took 41 min" — the finish stamp and the run's own duration.
 *
 * Both stamps come from the steps of one run, which is what `resolveSpeakingGroupId` on the
 * backend guarantees: the footer may only ever describe the revision on screen. A later group's
 * card stamp paired with this parse's start would describe a run that never happened.
 */
function buildFinishedFooter(
  processing: DocumentProcessingFacts | undefined,
  now: number
): string | null {
  const finishedAt = processing?.steps.card?.completedAt ?? processing?.steps.parse?.completedAt;
  if (finishedAt === undefined) return null;
  const startedAt = processing?.steps.parse?.startedAt;
  const finished = `Finished ${formatRelative(now, finishedAt)}`;
  if (startedAt === undefined || finishedAt < startedAt) return finished;
  return `${finished} · took ${formatElapsed(finishedAt - startedAt)}`;
}

/* -------------------------------------------------------------------------- */
/* Failed popover                                                             */
/* -------------------------------------------------------------------------- */

function buildFailedPopover(row: DocumentStatusSource, now: number): DocumentFailedPopover {
  const processing = row.processing;
  const failure = processing?.failure;
  const retryOffered = failure?.retryOffered ?? isDocumentRetryOffered(row.status);
  const rows: DocumentFactRow[] = [];

  if (failure) {
    rows.push({
      label: "Stopped during",
      value: FAILURE_STAGE_LABELS[failure.step],
      tone: "destructive",
    });
  }

  const probe = processing?.probe;
  const probeSpan = probe ? resolveProbeSpan(probe) : null;
  if (probeSpan) {
    rows.push({ label: probeSpan.label, value: formatCount(probeSpan.value), tone: "default" });
  }
  // The recording's own axis, in the slot a document's page count sits in. A media row never
  // has both — a probe of a recording counts no pages — so this is the same row, not a second.
  const recordingLength = formatRecordingLength(processing);
  if (recordingLength) rows.push({ label: "Length", value: recordingLength, tone: "default" });

  const sizeLabel = formatFileSize(probe?.fileSizeBytes ?? row.fileSizeBytes);
  if (sizeLabel) rows.push({ label: "Size", value: sizeLabel, tone: "default" });

  const failedAt = resolveFailedAt(processing, failure?.step);
  const startedAt = processing?.steps.parse?.startedAt;
  if (failedAt !== undefined && startedAt !== undefined && failedAt >= startedAt) {
    rows.push({ label: "Ran for", value: formatElapsed(failedAt - startedAt), tone: "default" });
  }

  // The status carries the taxonomy's sentence too, and on a surface whose query skips the
  // processing facts (the Attachments tab) it is the *only* copy of it. Reaching for it
  // before the generic sentence is what keeps "Couldn't read this file — it is password
  // protected." from degrading to "Processing failed." on one screen and not the other.
  const reason = failure?.reason ?? resolveStatusFailureMessage(row.status) ?? "Processing failed.";
  return {
    kind: "failed",
    title: row.name,
    embeddedIn: buildEmbeddedHeader(row),
    // "isn't readable yet" is only honest where a retry can still fix it — a deterministic
    // failure is as readable as it is ever going to be.
    sentence: retryOffered ? `${reason} ${DOCUMENT_PROCESSING_COPY.notReadableYet}` : reason,
    rows,
    // Only a fan-out has converted parts to reuse, so only a fan-out may promise a head start.
    retryNote:
      retryOffered && (processing?.chunks?.completed ?? 0) > 0
        ? DOCUMENT_PROCESSING_COPY.retryReusesParts
        : null,
    footer: failedAt === undefined ? null : `Failed ${formatRelative(now, failedAt)}`,
    retryOffered,
  };
}

/**
 * The failure sentence the *status* carries, for a source that has no processing facts.
 *
 * Everything else in the popover degrades by omission — a row nobody can compute is left
 * out rather than printed as a zero or a guess — and the sentence degrades the same way:
 * the taxonomy's own message where there is one, and only then the generic fallback.
 */
function resolveStatusFailureMessage(status: DocumentStatus): string | undefined {
  if (status.kind !== "failed") return undefined;
  const message = status.failure?.message;
  return message !== undefined && message.length > 0 ? message : undefined;
}

function resolveFailedAt(
  processing: DocumentProcessingFacts | undefined,
  stage: DocumentProcessingStage | undefined
): number | undefined {
  if (!processing) return undefined;
  const key = stage ? FAILURE_STAGE_STEPS[stage] : "parse";
  return processing.steps[key]?.completedAt ?? processing.steps.parse?.completedAt;
}

/** What the probe counted, in the file's own axis: pages, slides or worksheets. */
function resolveProbeSpan(probe: ProbeFacts): { label: string; value: number } | null {
  if (probe.pageCount !== undefined) return { label: "Pages", value: probe.pageCount };
  if (probe.slideCount !== undefined) return { label: "Slides", value: probe.slideCount };
  if (probe.sheets !== undefined) return { label: "Sheets", value: probe.sheets.length };
  if (probe.approxPageCount !== undefined) return { label: "Pages", value: probe.approxPageCount };
  return null;
}

/* -------------------------------------------------------------------------- */
/* Pre-read question                                                          */
/* -------------------------------------------------------------------------- */

function buildPreReadPopover(
  row: DocumentStatusSource,
  probeFromStatus: ProbeFacts | undefined
): DocumentPreReadPopover {
  const probe = probeFromStatus ?? row.processing?.probe;
  const format = probe?.format ?? "other";
  const sizeLabel = formatFileSize(probe?.fileSizeBytes ?? row.fileSizeBytes);
  const span = probe ? resolvePreReadSpan(format, probe) : null;

  return {
    kind: "pre_read",
    mode: "pre_read",
    artifactId: row.artifactId,
    title: row.name,
    format,
    prompt: DOCUMENT_PROCESSING_COPY.preReadPrompt,
    header: [span?.header, sizeLabel].filter((part): part is string => Boolean(part)).join(" · "),
    optionsHeading: DOCUMENT_PROCESSING_COPY.readScopeHeading,
    submitLabel: DOCUMENT_PROCESSING_COPY.startReading,
    options: buildReadScopeOptions(format, span),
    sheets: buildPreReadSheets(probe, []),
  };
}

function buildPreReadSheets(
  probe: ProbeFacts | undefined,
  alreadyRead: readonly string[]
): DocumentPreReadSheet[] {
  const read = new Set(alreadyRead);
  return (probe?.sheets ?? []).map((sheet) => ({
    name: sheet.name,
    index: sheet.index,
    hidden: sheet.hidden,
    rowsLabel: formatSheetRows(sheet.rows),
    alreadyRead: read.has(sheet.name),
  }));
}

/* -------------------------------------------------------------------------- */
/* Read more: the pre-read question, asked again                              */
/* -------------------------------------------------------------------------- */

/**
 * The question a partly-read document asks when somebody wants more of it.
 *
 * The same question the file was asked before anything was read, with four differences that
 * all follow from one fact — part of it *is* read:
 *
 * - The header states what that part is ("Pages 1–55 of 3,590 read") instead of what the file
 *   is; the person can see the size on the row.
 * - The range starts at the first page nobody has read and runs to the end of the file, so
 *   pressing Read more without touching anything reads the remainder. It is a default, not a
 *   rule: a range typed over it is honoured, and the backend unions it with what was read.
 * - **"Text only" is not offered.** It is a depth scope, and depth is not something a span can
 *   widen: answering it here would ask for *less* than the document already holds.
 * - Already-read worksheets are ticked and locked. Unticking one would be asking to unread it,
 *   which no answer can do — so the tick states the fact rather than offering a choice.
 *
 * `null` for a document that is not scope-limited, which is what keeps the offer and the
 * question in agreement: the row shows "Read more…" only where `readTheRest === "scope"`.
 */
export function buildReadMoreQuestion(row: DocumentStatusSource): DocumentPreReadPopover | null {
  const processing = row.processing;
  if (processing?.readTheRest !== "scope") return null;
  const probe = processing.probe;
  const format = probe?.format ?? "other";
  const span = probe ? resolvePreReadSpan(format, probe) : null;
  const read = resolveReadSoFar(processing);

  return {
    kind: "pre_read",
    mode: "extend",
    artifactId: row.artifactId,
    title: row.name,
    format,
    prompt: DOCUMENT_PROCESSING_COPY.readMorePrompt,
    header: read.header,
    optionsHeading: DOCUMENT_PROCESSING_COPY.readMoreHeading,
    submitLabel: DOCUMENT_PROCESSING_COPY.readMoreSubmit,
    options: buildReadMoreOptions(format, span, read.nextStart),
    sheets: buildPreReadSheets(probe, read.sheetNames),
  };
}

/**
 * What has been read, in the words the header uses and the number the range box starts from.
 *
 * Read off the *coverage* first and the scope second, and in that order deliberately: the
 * coverage is what the run achieved, the scope is what it was asked for, and a run somebody
 * stopped part-way achieved less than it was asked. Where neither can place a page — a workbook, a
 * parse too old to record coverage — the header says the general thing rather than a number it
 * would be inventing.
 */
function resolveReadSoFar(processing: DocumentProcessingFacts): {
  header: string;
  /** The first page or slide nobody has read, when there is a page axis to count on. */
  nextStart?: number;
  /** Worksheets already read, for the locked ticks. */
  sheetNames: string[];
} {
  const scope = processing.readScope;
  if (scope?.kind === "sheets") {
    return {
      header: `${formatCount(scope.names.length)} of ${formatCount(
        processing.probe?.sheets?.length ?? scope.names.length
      )} sheets read`,
      sheetNames: [...scope.names],
    };
  }

  const covered = processing.completeness?.coverage?.coveredPageRanges ?? [];
  const ranges =
    covered.length > 0 ? covered : scope?.kind === "pages" ? readScopePageRanges(scope) : [];
  const unit = processing.probe?.format === "pptx" ? "Slides" : "Pages";
  const total = processing.completeness?.coverage?.totalPages ?? resolveProbeTotal(processing);
  const last = ranges[ranges.length - 1];
  if (!last) {
    return { header: "Part of this document is read", sheetNames: [] };
  }
  const ofTotal = total === undefined ? "" : ` of ${formatCount(total)}`;
  return {
    // Every span, so the person choosing what to read next can see the hole they are choosing
    // around rather than a bound that hides it.
    header: `${unit} ${formatPageRanges(ranges)}${ofTotal} read`,
    // One past the furthest page read: the remainder starts there, whatever holes sit behind it.
    // A file already read to its end has no remainder and offers no default.
    ...(total !== undefined && last.end >= total ? {} : { nextStart: last.end + 1 }),
    sheetNames: [],
  };
}

function resolveProbeTotal(processing: DocumentProcessingFacts): number | undefined {
  const probe = processing.probe;
  if (probe?.format === "pptx") return probe.slideCount;
  return probe?.pageCount ?? probe?.approxPageCount;
}

/**
 * The answers the second asking offers: the whole file, or more of the axis it has.
 *
 * No "Text only", and no "Delete upload" beside it — see `DocumentReadScopeMode`.
 */
function buildReadMoreOptions(
  format: DocumentProbeFormat,
  span: PreReadSpan | null,
  nextStart: number | undefined
): DocumentReadScopeOption[] {
  const everything: DocumentReadScopeOption = {
    kind: "everything",
    label: "Everything",
    description: `${span?.everything ?? "The whole document"}, with pictures and page photos. This will take a while.`,
  };

  if (format === "xlsx") {
    return [everything, { kind: "sheets", label: "Pick sheets", description: null }];
  }

  if ((format === "pdf" || format === "pptx") && span?.range) {
    const isSlides = format === "pptx";
    const defaultStart = Math.min(
      Math.max(nextStart ?? span.range.min, span.range.min),
      span.range.max
    );
    return [
      everything,
      {
        kind: isSlides ? "slides" : "pages",
        label: isSlides ? "Only slides" : "Only pages",
        description: "Pictures inside that range are still read.",
        range: {
          min: span.range.min,
          max: span.range.max,
          // To the end of the file rather than another 500-page bite: the person has already
          // seen how long the first span took and is answering with that in hand.
          defaultStart,
          defaultEnd: span.range.max,
        },
      },
    ];
  }

  // Word, and any file the probe could not measure: there is no axis to select on, so the only
  // answer left is the whole thing.
  return [everything];
}

type PreReadSpan = {
  /** "3,000 pages", "412 slides", "14 sheets", "≈ 380 pages". */
  header: string;
  /** What "Everything" promises: "All 3,000 pages" / "The whole document". */
  everything: string;
  /** Bounds for a range answer. Absent for a format with no page axis. */
  range?: { min: number; max: number };
};

function resolvePreReadSpan(format: DocumentProbeFormat, probe: ProbeFacts): PreReadSpan | null {
  if (format === "pptx" && probe.slideCount !== undefined) {
    return {
      header: `${formatCount(probe.slideCount)} slides`,
      everything: `All ${formatCount(probe.slideCount)} slides`,
      range: { min: 1, max: probe.slideCount },
    };
  }
  if (format === "xlsx" && probe.sheets !== undefined) {
    return {
      header: `${formatCount(probe.sheets.length)} sheets`,
      everything: `All ${formatCount(probe.sheets.length)} sheets`,
    };
  }
  if (format === "docx") {
    // The authoring app's own estimate, and the design says so with a "≈" rather than
    // pretending a layout was measured.
    if (probe.approxPageCount === undefined) return null;
    return {
      header: `≈ ${formatCount(probe.approxPageCount)} pages`,
      everything: "The whole document",
    };
  }
  if (probe.pageCount !== undefined) {
    return {
      header: `${formatCount(probe.pageCount)} pages`,
      everything: `All ${formatCount(probe.pageCount)} pages`,
      range: { min: 1, max: probe.pageCount },
    };
  }
  return null;
}

const TEXT_ONLY_OPTION: DocumentReadScopeOption = {
  kind: "text_only",
  label: "Text only",
  description: "Skip pictures and page photos. Much faster.",
};

/**
 * The answers this format can actually give.
 *
 * "Pick sheets" is deliberately last on a workbook: a fifty-tab workbook's sheet list must
 * not sit in the way of the two simple answers.
 */
function buildReadScopeOptions(
  format: DocumentProbeFormat,
  span: PreReadSpan | null
): DocumentReadScopeOption[] {
  const everything: DocumentReadScopeOption = {
    kind: "everything",
    label: "Everything",
    description: `${span?.everything ?? "The whole document"}, with pictures and page photos. This will take a while.`,
  };

  if (format === "xlsx") {
    return [
      everything,
      TEXT_ONLY_OPTION,
      { kind: "sheets", label: "Pick sheets", description: null },
    ];
  }

  if ((format === "pdf" || format === "pptx") && span?.range) {
    const isSlides = format === "pptx";
    return [
      everything,
      {
        kind: isSlides ? "slides" : "pages",
        label: isSlides ? "Only slides" : "Only pages",
        description: "Pictures inside that range are still read.",
        range: {
          min: span.range.min,
          max: span.range.max,
          defaultStart: span.range.min,
          defaultEnd: Math.min(span.range.max, PRE_READ_SUGGESTED_SPAN),
        },
      },
      TEXT_ONLY_OPTION,
    ];
  }

  // Word, and any file the probe could not measure: there is no axis to select on, so the
  // only honest narrowing left is dropping the pictures.
  return [everything, TEXT_ONLY_OPTION];
}

/* -------------------------------------------------------------------------- */
/* Row menu                                                                   */
/* -------------------------------------------------------------------------- */

export type DocumentRowMenuItemKind =
  | "ask_claudia"
  | "rename"
  /** Open a recording's transcript, read as turns. Recordings only, and only once read. */
  | "view_transcript"
  /** Open the file itself in the app — the same dialog the NAME opens. */
  | "preview"
  /**
   * Hand over the file's bytes. For a recording this is the *audio* item — the original for an
   * audio upload, the extracted audio for a transcribed video — and the video item below carries
   * the original instead.
   */
  | "download"
  /** A video recording's own bytes, under its own name. Video recordings only. */
  | "download_video"
  /** Save that same transcript as a plain-text file. Offered beside the viewer, never alone. */
  | "download_transcript"
  /** Open the Drive file this document was imported from, at Google. */
  | "open_in_google"
  /** Ask Google whether the imported file has changed, and read the new version if it has. */
  | "refresh_from_google"
  /** A span is missing: opens the question again. */
  | "read_more"
  /** The span is whole and the pictures were cut: one press, no question. */
  | "read_the_rest"
  | "reprocess"
  | "stop_processing"
  /** End a long read at its next part boundary, keeping the parts already read. */
  | "stop_and_keep"
  | "try_again"
  | "delete";

export type DocumentRowMenuItem = {
  kind: DocumentRowMenuItemKind;
  label: string;
  /** Present but not pressable — for Ask Claudia, the greying *is* the message. */
  disabled: boolean;
  tone: "default" | "destructive";
  /** Draw a divider above this item. Only Delete asks for one. */
  separated: boolean;
};

/**
 * A Ready file worth reading again from scratch.
 *
 * Two facts, one repair. Either this deployment would parse the file differently today
 * (`processingOutdated` — a capability it has since enabled, a parser or transform bump), or the
 * revision on screen holds pictures whose stored images are gone
 * (`isDocumentPicturesUnavailable`). The second is invisible to the first: the revision's key is
 * exactly the key a parse would compute today, so nothing about its *identity* is out of date —
 * only its content is damaged, and "Read more…" replays the parts holding those pictures
 * unchanged. A full re-read is the only thing that puts them back.
 */
export function isReprocessOffered(
  row: Pick<ProjectDocumentRow, "status" | "processing">
): boolean {
  if (row.status.kind !== "ready") return false;
  return row.status.processingOutdated === true || isDocumentPicturesUnavailable(row.processing);
}

/**
 * A document whose readable words are turns — the one condition every transcript item shares.
 *
 * `contentShape` is the server's own answer about the blocks behind the active revision, so a
 * recording and an uploaded transcript file are one offer and nothing here reads a file extension
 * to decide.
 *
 * `parsed` is the other load-bearing half: the shape describes a revision, and `readySource`
 * is what says one is actually active. A `no_parsing_needed` ready row is a file nothing was ever
 * going to read, and offering to open a transcript of it would open an empty dialog. Downloading
 * the audio is deliberately *not* gated on this: a recording's bytes are there from the upload,
 * whatever became of the transcription.
 */
export function isTranscriptOffered(
  row: Pick<ProjectDocumentRow, "status" | "contentShape">
): boolean {
  return (
    row.status.kind === "ready" &&
    row.status.readySource === "parsed" &&
    row.contentShape === "transcript"
  );
}

/** Which of a recording's two objects "Download audio" hands over. */
export type MediaDownloadVariant = "original" | "derived_audio";

/**
 * The bytes "Download audio" gives a person, or `null` where the item is not offered at all.
 *
 * One rule, read twice — the builder decides whether the item exists by it and the menu decides
 * what to ask the backend for by it — because a menu offering an item the download then refuses
 * is the two halves of one decision drifting apart.
 *
 * An **audio** upload's audio is the file itself, and it is there from the moment it uploaded. A
 * **video's** is not: what the lane keeps is the mono 16 kHz Opus it extracted to transcribe, and
 * that object only exists once a transcription has succeeded — which is exactly when the
 * transcript is offered. So the video half is gated on `isTranscriptOffered` and an untranscribed
 * video is offered its own bytes and nothing else.
 */
export function resolveAudioDownloadVariant(
  row: Pick<ProjectDocumentRow, "status" | "fileName" | "mimeType" | "contentShape">
): MediaDownloadVariant | null {
  const fileClass = recordingClass(row);
  if (fileClass === "audio") return "original";
  if (fileClass !== "video") return null;
  return isTranscriptOffered(row) ? "derived_audio" : null;
}

/**
 * Whether this row is an audio recording, a video recording, or neither — from the same
 * classifier the upload ceilings and the media-storage routing already use, so nothing here
 * sniffs a MIME type of its own.
 */
export function recordingClass(row: {
  fileName?: string;
  mimeType?: string | null;
}): "audio" | "video" | null {
  if (row.fileName === undefined) return null;
  const fileClass = classifyArtifactFile(row.fileName, row.mimeType ?? undefined);
  return fileClass === "audio" || fileClass === "video" ? fileClass : null;
}

/**
 * The ⋯ menu for one document, per state.
 *
 * One builder rather than five, because the variants differ by three decisions — is there
 * readable text, is a run still going, is there a remainder — and a menu assembled in JSX
 * per state is five copies of the same order for someone to get wrong.
 */
export function resolveDocumentRowMenu(row: ProjectDocumentRow): DocumentRowMenuItem[] {
  const status = row.status;
  const isReady = status.kind === "ready";
  const isRunning =
    status.kind === "queued" || status.kind === "parsing" || status.kind === "summarizing";
  const isWaiting = status.kind === "waiting_for_input";

  const isRecording = isRecordingRow(row);
  const transcriptOffered = isTranscriptOffered(row);

  const items: DocumentRowMenuItem[] = [
    {
      kind: "ask_claudia",
      label: "Ask Claudia",
      // Nothing has read the file yet, so there is nothing for Claudia to answer from.
      disabled: !isReady,
      tone: "default",
      separated: false,
    },
    { kind: "rename", label: "Rename", disabled: false, tone: "default", separated: false },
  ];

  // Ahead of the ways to get the file, because it is the same thing without leaving the screen.
  // The item exists exactly where the NAME opens the dialog: one resolver answers both, so the
  // menu can never offer a preview the name refuses, or the other way round.
  const openAction = resolveDocumentOpenAction(documentOpenSourceFromRow(row));
  if (openAction?.kind === "preview") {
    items.push({
      kind: "preview",
      label: documentPreviewMenuLabel(openAction.previewKind),
      disabled: false,
      tone: "default",
      separated: false,
    });
  }

  // Read the words, then keep the words — ahead of every way to get the container they arrived
  // in, because the words are what a person came to the row for. The pair belongs to the *shape*
  // of the active revision rather than to a file class, so a recording, a subtitle file and a
  // Word transcript are one offer; nothing here reads an extension.
  if (transcriptOffered) {
    items.push({
      kind: "view_transcript",
      label: "View transcript",
      disabled: false,
      tone: "default",
      separated: false,
    });
    items.push({
      kind: "download_transcript",
      label: "Download transcript",
      disabled: false,
      tone: "default",
      separated: false,
    });
  }

  if (isRecording) {
    // The two media downloads are a recording's alone, and they are named for what the bytes
    // *are* rather than for how far along the run is — an audio file and a video file are
    // different things to ask for, and a recording's bytes exist from the upload whatever became
    // of the transcription.
    //
    // Offered only where there is audio to hand over: an untranscribed video's soundtrack is
    // inside its container and nothing here can take it out (`resolveAudioDownloadVariant`).
    if (resolveAudioDownloadVariant(row) !== null) {
      items.push({
        kind: "download",
        label: "Download audio",
        disabled: false,
        tone: "default",
        separated: false,
      });
    }
    if (recordingClass(row) === "video") {
      items.push({
        kind: "download_video",
        label: "Download video",
        disabled: false,
        tone: "default",
        separated: false,
      });
    }
  } else {
    // Where Google owns the document, this is the way to it — and the only way offered. Omitted
    // rather than greyed where Google gave no link: an item that cannot open anything is worse
    // than no item.
    if (row.externalSource?.webViewLink) {
      items.push({
        kind: "open_in_google",
        label: resolveGoogleOpenLabel(row.externalSource),
        disabled: false,
        tone: "default",
        separated: false,
      });
    }

    // A document Google owns is offered no download at all. What this app holds of it is a Word,
    // Excel or PowerPoint file made so the pipeline could read it, and handing a person that copy
    // of their own Google Doc is not giving them the document — the item above is. A file Drive
    // merely stored keeps its Download: its bytes *are* the document.
    const isProviderOwned = row.externalSource ? isGoogleOwnedDocument(row.externalSource) : false;
    if (!isProviderOwned) {
      items.push({
        kind: "download",
        // "Download original" while a run is in flight, because the bytes are all that exists yet.
        label: isReady ? "Download" : "Download original",
        // A note or a pasted snippet has no bytes at all, and its item is present but dead.
        disabled: row.downloadUrl === null,
        tone: "default",
        separated: false,
      });
    }
  }

  // Beneath it, and for every imported row rather than only the linkable ones: refreshing needs
  // the file's identity, which every import has, while opening it needs a link Google may not
  // have given. A document already busy is greyed by the menu, like every other item that starts
  // a job — a refresh that finds a newer version starts a read, and there must not be two.
  if (row.externalSource) {
    items.push({
      kind: "refresh_from_google",
      label: resolveGoogleRefreshLabel(row.externalSource),
      disabled: false,
      tone: "default",
      separated: false,
    });
  }

  // Two offers, two items, and the wording is the whole difference: one opens a question, the
  // other starts a run.
  const offer = resolveReadTheRest(row.processing);
  const offerItem: DocumentRowMenuItem | null = offer
    ? {
        kind: offer === "scope" ? "read_more" : "read_the_rest",
        label:
          offer === "scope"
            ? DOCUMENT_PROCESSING_COPY.readMore
            : DOCUMENT_PROCESSING_COPY.readAllPictures,
        disabled: false,
        tone: "default",
        separated: false,
      }
    : null;
  const reprocessItem: DocumentRowMenuItem = {
    kind: "reprocess",
    label: "Reprocess",
    disabled: false,
    tone: "default",
    separated: false,
  };

  // A read whose pictures cannot be opened is never fixed by either offer above — "Read more…"
  // resumes the very parts holding those pictures unchanged, and "Read all pictures" re-reads
  // without touching a stored image that is simply gone — so Reprocess leads them here. It is
  // the one case `isReprocessOffered` can be true for a row that also carries an offer: the
  // other, an outdated parse, stays behind the offer below because "Read more…"/"Read all
  // pictures" are still the cheaper, more specific answer there.
  const reprocessBeforeOffer =
    isReady && isDocumentPicturesUnavailable(row.processing) && isReprocessOffered(row);
  if (reprocessBeforeOffer) items.push(reprocessItem);
  if (offerItem) items.push(offerItem);

  if (isRunning || isWaiting) {
    items.push({
      kind: "stop_processing",
      label: "Stop processing",
      disabled: false,
      tone: "default",
      separated: false,
    });
    // Only where there is something to keep and something still to stop. A read that has
    // finished no part has no span the backend could describe; one that has finished every part
    // is merging the whole document with no part boundary left to honour the request at; and
    // `stopAndKeepSupported` is the backend's own answer for a plan holding no unit a revision
    // could claim, such as a workbook read half-way down its only sheet. Each would be a menu
    // entry whose only outcome is a refusal, and Stop processing is the honest answer for all
    // three.
    const chunks = row.processing?.chunks;
    if (chunks?.stopAndKeepSupported && chunks.completed >= 1 && chunks.completed < chunks.total) {
      items.push({
        kind: "stop_and_keep",
        label: DOCUMENT_PROCESSING_COPY.stopAndKeep,
        // Already asked for, and the part boundary that honours it is at most one part away.
        disabled: chunks.stopping === true,
        tone: "default",
        separated: false,
      });
    }
  } else if (isDocumentRetryOfferedForFile(status, row)) {
    items.push({
      kind: "try_again",
      label: DOCUMENT_PROCESSING_COPY.tryAgain,
      disabled: false,
      tone: "default",
      separated: false,
    });
  } else if (isReprocessOffered(row) && !reprocessBeforeOffer) {
    items.push(reprocessItem);
  }

  items.push({
    kind: "delete",
    label: "Delete",
    disabled: false,
    tone: "destructive",
    separated: true,
  });
  return items;
}

/* -------------------------------------------------------------------------- */
/* Waiting-for-you banner                                                     */
/* -------------------------------------------------------------------------- */

export type DocumentWaitingBannerView = {
  /** "1 document needs your decision". */
  title: string;
  /** "Nothing is read until you answer. 2 other documents are processing." */
  detail: string;
  actionLabel: string;
};

export type AwaitingInputCount = {
  count: number;
  /** The count hit its server-side cap; the real number is higher. */
  more: boolean;
};

/**
 * The banner over a table that holds documents nobody has answered for.
 *
 * The banner never acts on a file — the decision belongs to a document and only its row can
 * carry it — so this composes a count and a filter invitation, and nothing else. `null` when
 * nothing is waiting, which is what lets the processing summary have the slot back.
 */
export function resolveWaitingBanner(
  rows: readonly ProjectDocumentRow[],
  count: AwaitingInputCount
): DocumentWaitingBannerView | null {
  if (count.count <= 0) return null;

  const processingCount = rows.filter((row) =>
    ["queued", "parsing", "summarizing"].includes(row.status.kind)
  ).length;

  const title = count.more
    ? `${formatCount(count.count)}+ documents need a decision`
    : count.count === 1
      ? "1 document needs your decision"
      : `${formatCount(count.count)} documents need your decision`;

  const alsoProcessing =
    processingCount === 1
      ? " 1 other document is processing."
      : processingCount > 1
        ? ` ${formatCount(processingCount)} other documents are processing.`
        : "";

  return {
    title,
    detail: `Nothing is read until you answer.${alsoProcessing}`,
    actionLabel: DOCUMENT_PROCESSING_COPY.showOnlyWaiting,
  };
}

/** "Waiting for you · 3" — the filter chip the banner's button leaves behind. */
export function formatWaitingFilterChipLabel(count: number): string {
  return `${DOCUMENT_PROCESSING_COPY.waitingFilterChip} · ${formatCount(count)}`;
}

/* -------------------------------------------------------------------------- */
/* Refusal copy                                                               */
/* -------------------------------------------------------------------------- */

/**
 * What a refused answer says, keyed by the `details.reason` the backend sends.
 *
 * The mutations already carry person-facing sentences, and this map does not replace them —
 * it names the ones the *screen* can say better, because the screen knows what the person
 * was doing: a range the file does not have is a correction to make in the inputs, not a
 * failure to report. Anything not named here degrades to the backend's own sentence.
 */
export const DOCUMENT_READ_SCOPE_ERROR_COPY = {
  read_scope_not_awaiting: "This document isn't waiting for an answer any more.",
  read_scope_unsupported: "This file has no pages or sheets to choose from.",
  read_scope_out_of_range: "That range is outside this document.",
  read_scope_unknown_sheet: "That worksheet isn't in this workbook any more.",
  read_scope_hidden_sheet: "Hidden sheets can't be read — pick a visible one.",
  read_the_rest_nothing_to_read: "This document is already as fully read as it can be.",
  // Only reachable from a stale row: the item is withdrawn once every part has landed, so a
  // press that gets this far was made against a menu drawn a moment before the last part did.
  read_scope_nothing_left_to_stop: "Every part is already read; the document is finishing.",
  // Only reachable from a stale row: the offer was `limit` when the menu was drawn and is
  // `scope` by the time the press lands. The sentence names the item that is there now.
  read_more_required: "Use Read more… to choose how much more of this document to read.",
  // "Read more…" never offers Text only (it widens a span; depth is a different axis), so this
  // is only reachable from a client that composed the answer itself.
  read_scope_not_a_span: "Text only doesn't widen a span. Choose a range instead.",
  read_scope_already_read: "That's already read — choose a range or selection beyond it.",
  artifact_name_empty: "A name can't be empty.",
  artifact_rename_too_long: "That name is too long.",
  artifact_rename_extension_changed: "The file extension can't change.",
  artifact_rename_not_project_scoped: "This file isn't part of a project and can't be renamed.",
  // Speaker names belong to one transcription, so a viewer open across a re-transcription is
  // naming numbers that describe nobody on screen. Reopening it is the whole repair.
  transcript_revision_superseded:
    "This recording has been transcribed again. Reopen the transcript to name its speakers.",
} as const satisfies Record<string, string>;

/**
 * The sentence to show for a refused document action.
 *
 * The screen's own wording where it has one, the backend's where it does not, and the
 * caller's fallback for an error that is not a coded refusal at all — one lookup, so five
 * hooks cannot each answer the question differently.
 */
export type DocumentAnswerOutcome =
  | { ok: true }
  /**
   * The sentence to show — inline, beside whatever has to change — or `null` where the
   * answer never left the screen: a second press on a question already being answered is
   * refused by the claim rather than by the server, and reporting it would explain a state
   * the screen is already showing.
   */
  | { ok: false; message: string | null };

export function resolveDocumentRefusalMessage(error: unknown, fallback: string): string {
  const data = getAppErrorData(error);
  const reason = data?.details?.reason;
  // The reason arrives as an arbitrary string from the wire, so the lookup is widened rather
  // than the map: the keys above stay literal for every call site that names one.
  const known = (DOCUMENT_READ_SCOPE_ERROR_COPY as Record<string, string | undefined>)[
    typeof reason === "string" ? reason : ""
  ];
  if (known) return known;
  return getErrorMessage(error, fallback);
}

/* -------------------------------------------------------------------------- */
/* Composing the pre-read answer                                              */
/* -------------------------------------------------------------------------- */

/** "3 of 14 selected" — the sheet picker's own header. */
export function formatSheetSelectionLabel(selected: number, total: number): string {
  return `${formatCount(selected)} of ${formatCount(total)} selected`;
}

/**
 * The answer the panel opens on, before anybody has said anything.
 *
 * A first asking opens on the first option, which is "Everything": nothing has been read, so
 * the whole file is the answer that needs no thought. A second asking opens on the *range*,
 * because that is the question it is asking — "how much more" — and its boxes already hold the
 * remainder. Opening it on "Everything" beside a range box pre-filled with pages 56–3,590 would
 * be two answers on screen at once, one of them ticked and the other one written out.
 *
 * A format with no axis to select on (a workbook, a Word file) has no range option to open on,
 * and falls back to the same first option as a first asking.
 */
export function defaultReadScopeKind(
  payload: Pick<DocumentPreReadPopover, "mode" | "options">
): DocumentReadScopeOptionKind {
  const range =
    payload.mode === "extend"
      ? payload.options.find((option) => option.range !== undefined)
      : undefined;
  return range?.kind ?? payload.options[0]?.kind ?? "everything";
}

/** What the popover is holding when the person presses "Start reading". */
export type ReadScopeDraft = {
  kind: DocumentReadScopeOptionKind;
  /** Raw input text, not numbers: an empty box and a typo are states the person can be in. */
  start: string;
  end: string;
  /** Worksheet names, as ticked. */
  sheets: readonly string[];
};

export type ReadScopeAnswerResult =
  | { ok: true; answer: ReadScope }
  | { ok: false; message: string };

/**
 * The draft turned into the answer the backend takes, or the correction to make first.
 *
 * Client-side validation exists here rather than in the component for the usual reason —
 * the sentences are copy — but also because these are the *same* refusals the mutation
 * raises, and saying them before the round trip and after it in two different wordings is
 * how a form teaches people that the screen and the server disagree. Every message below is
 * either one of `DOCUMENT_READ_SCOPE_ERROR_COPY`'s or a sentence for a case the backend
 * cannot reach (an empty box never leaves the browser).
 *
 * A slide range answers `{kind:"pages"}`: `ReadScope` has one span kind, and a deck's slides
 * *are* its pages as far as the reader is concerned.
 */
export function composeReadScopeAnswer(
  draft: ReadScopeDraft,
  payload: Pick<DocumentPreReadPopover, "options" | "sheets">
): ReadScopeAnswerResult {
  if (draft.kind === "everything") return { ok: true, answer: { kind: "everything" } };
  if (draft.kind === "text_only") return { ok: true, answer: { kind: "text_only" } };

  if (draft.kind === "sheets") {
    if (draft.sheets.length === 0) {
      return { ok: false, message: READ_SCOPE_FORM_COPY.noSheetPicked };
    }
    const byName = new Map(payload.sheets.map((sheet) => [sheet.name, sheet]));
    for (const name of draft.sheets) {
      const sheet = byName.get(name);
      if (!sheet) {
        return { ok: false, message: DOCUMENT_READ_SCOPE_ERROR_COPY.read_scope_unknown_sheet };
      }
      if (sheet.hidden) {
        return { ok: false, message: DOCUMENT_READ_SCOPE_ERROR_COPY.read_scope_hidden_sheet };
      }
    }
    return { ok: true, answer: { kind: "sheets", names: [...draft.sheets] } };
  }

  const range = payload.options.find((option) => option.kind === draft.kind)?.range;
  if (!range) {
    return { ok: false, message: DOCUMENT_READ_SCOPE_ERROR_COPY.read_scope_unsupported };
  }
  const start = parseWholeNumber(draft.start);
  const end = parseWholeNumber(draft.end);
  if (start === null || end === null) {
    return { ok: false, message: READ_SCOPE_FORM_COPY.rangeNotANumber };
  }
  if (start < range.min || end > range.max) {
    return { ok: false, message: DOCUMENT_READ_SCOPE_ERROR_COPY.read_scope_out_of_range };
  }
  if (start > end) {
    return { ok: false, message: READ_SCOPE_FORM_COPY.rangeBackwards };
  }
  return { ok: true, answer: { kind: "pages", start, end } };
}

/**
 * The three refusals only the browser can raise.
 *
 * They are not in `DOCUMENT_READ_SCOPE_ERROR_COPY` because that map is keyed by the
 * backend's `details.reason` codes, and none of these ever reaches the backend: an empty
 * box, a word where a number goes, and a range typed the wrong way round are all stopped
 * here.
 */
export const READ_SCOPE_FORM_COPY = {
  noSheetPicked: "Pick at least one sheet.",
  rangeNotANumber: "Fill in both numbers.",
  rangeBackwards: "The first number has to be the smaller one.",
} as const;

function parseWholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}
